import {MdCrossLine, MdFrameBlock, MdFrameBlockChild, MdFrameItem, MdFrameJoint, MdFrameStick, MdGlazing, MdPanelMaterial, MdPanelPosition, forcedProfile, joinedRootId, stepVoids, sticksById} from "../../model/frame/MdFrameBlock";
import {MdGlass} from "../../model/glass/MdGlass";
import {MdFrameProfileType, MdFrameSpec, primaryDoorsItem, wireDoorsItem} from "../../model/frame/MdFrameSpec";
import {FrmVar} from "../../drawfrm/FrmVar";
import {DEFAULT_AIR_GAP, makeMullionThicknessAbsorb, MIN_AIR_GAP, MIN_BLOCK, stickThicknessVar, VOID_ZERO} from "./Util";
import {inches} from "../../const";

// default width (row) / height (column) an edge-inserted lite is seeded with, so a
// freshly added lite reads at a usable size and the frame grows to fit rather than
// the solver collapsing it to its minimum. splitItem opts out (passes null) — its
// halves are sized from the split cell's footprint instead.
const NEW_LITE_SIZE = 8 * inches;

// in-place edits to the frame block tree (spec.frameBlocks) driven by the
// ClickableFace menu. Callers emit frameSpec.frameBlocksChanged afterwards so the
// grid view rebuilds against the mutated model.

type PathStep = {block: MdFrameBlock; index: number};

// path from the root down to the item: each step descends into
// step.block.children[step.index], and the last step's child IS the item.
function findItemPath(root: MdFrameBlock, itemId: string): PathStep[] | null {
    for(let i = 0; i < root.children.length; i++) {
        const c = root.children[i];
        if(c.kind === "item" && c.item.id === itemId) {
            return [{block: root, index: i}];
        }
        if(c.kind === "block") {
            const sub = findItemPath(c.block, itemId);
            if(sub) return [{block: root, index: i}, ...sub];
        }
    }

    return null;
}

// like findItemPath, but the target may be a stick as well as an item — used by
// removeItem, which removes either. The last step's child is the matched node.
function findChildPath(root: MdFrameBlock, id: string): PathStep[] | null {
    for(let i = 0; i < root.children.length; i++) {
        const c = root.children[i];
        if((c.kind === "item" && c.item.id === id) || (c.kind === "stick" && c.stick.id === id)) {
            return [{block: root, index: i}];
        }
        if(c.kind === "block") {
            const sub = findChildPath(c.block, id);
            if(sub) return [{block: root, index: i}, ...sub];
        }
    }

    return null;
}

// a cell is anything that occupies a grid slot (item or nested block); sticks are
// the separators/mullions between cells.
function isBlockEmpty(block: MdFrameBlock): boolean {
    return !block.children.some(c => c.kind === "item" || c.kind === "block");
}

// a cell (item or nested block) whose subtree contains a door. Such a cell is
// never removed, and a stick bordering one offers no remove (see canRemoveStick).
function cellContainsDoor(child: MdFrameBlockChild | undefined): boolean {
    if(!child) return false;
    if(child.kind === "item") return child.item.kind === "doors";
    if(child.kind === "block") return child.block.children.some(cellContainsDoor);
    return false;
}

// a cell that may be removed: occupies a slot and holds no door.
function isRemovableCell(child: MdFrameBlockChild | undefined): boolean {
    return isCell(child) && !cellContainsDoor(child);
}

// remove the cell at children[idx] plus the single separator stick that joined it
// to a neighbour (prefer the following mullion, else the preceding one). This
// keeps exactly one mullion between the surviving neighbours and never leaves a
// stick dangling against no cell. The block's own edge sticks stay: a lone cell
// goes on its own, and the emptied block with it.
function removeCellAt(block: MdFrameBlock, idx: number) {
    const children = block.children;

    let stickIdx = -1;
    if(children[idx + 1]?.kind === "stick" && isCell(children[idx + 2])) stickIdx = idx + 1;
    else if(children[idx - 1]?.kind === "stick" && isCell(children[idx - 2])) stickIdx = idx - 1;

    // splice the higher index first so the lower index stays valid.
    [idx, stickIdx]
        .filter(i => i >= 0)
        .sort((a, b) => b - a)
        .forEach(i => children.splice(i, 1));
}

// remove a stick plus an adjacent removable cell (prefer the following one) so the
// gap closes rather than leaving two cells abutting. A door neighbour is never the
// one removed. Returns false when neither neighbour may be removed (e.g. doors sit
// on both sides).
function removeStickWithNeighbour(block: MdFrameBlock, stickIdx: number): boolean {
    const children = block.children;

    let cellIdx = -1;
    if(isRemovableCell(children[stickIdx + 1])) cellIdx = stickIdx + 1;
    else if(isRemovableCell(children[stickIdx - 1])) cellIdx = stickIdx - 1;
    else return false;

    // splice the higher index first so the lower index stays valid.
    [stickIdx, cellIdx].sort((a, b) => b - a).forEach(i => children.splice(i, 1));
    return true;
}

// unwrap wrapper blocks left holding a single cell after a removal (the shape
// splitItem / insertAcrossPerimeter create). The cell takes over the wrapper's
// slot, and with it the wrapper's flow size — that var described the slot's extent
// along the parent's flow, which is exactly what the cell's size means there.
function collapseIn(block: MdFrameBlock) {
    for(const c of block.children) {
        if(c.kind === "block") collapseIn(c.block);
    }

    for(let i = 0; i < block.children.length; i++) {
        const c = block.children[i];
        if(c.kind !== "block") continue;

        const inner = c.block;
        if(inner.children.some(cc => cc.kind === "stick")) continue;
        const cells = inner.children.filter(isCell);
        if(cells.length !== 1) continue;

        const cell = cells[0];
        if(cell.kind === "item" && cell.item.kind !== "doors") {
            cell.item.size = inner.size;
            cell.item.size.rename(cell.item.id + ":size");
        } else if(cell.kind === "block") {
            cell.block.size = inner.size;
            cell.block.size.rename(cell.block.id + ":size");
        }
        block.children[i] = cell;
    }
}

// collapse single-cell wrappers everywhere, then un-wrap the root itself when it's
// left holding just one block and no sticks: that block becomes the root.
function collapseWrappers(spec: MdFrameSpec) {
    collapseIn(spec.frameBlocks);

    const root = spec.frameBlocks;
    if(root.children.some(c => c.kind === "stick")) return;
    const cells = root.children.filter(isCell);
    if(cells.length !== 1 || cells[0].kind !== "block") return;

    spec.frameBlocks = cells[0].block;
}

// remove a cell or a stick from the tree by id, then collapse any block left
// empty by the removal (recursively, up to the root).
//   - a cell (item / nested block): drop it plus the separator stick that joined
//     it to a neighbour.
//   - a stick (interior mullion or edge-override): drop it plus an adjacent
//     removable lite/panel cell so the gap closes. A door is never removed, so we
//     refuse if no removable neighbour exists.
//   - a perimeter stick (outer frame border): collapse the frame inward — drop the
//     edge cell it abuts plus that cell's mullion. The border itself stays, now
//     against the next cell (see perimeterRemoval for when this is allowed).
export function removeItem(spec: MdFrameSpec, id: string): boolean {
    const peri = perimeterRemoval(spec, id);
    if(peri) {
        removeCellAt(peri.block, peri.cellIdx);
        collapseWrappers(spec);
        return true;
    }

    const root = spec.frameBlocks;
    const path = findChildPath(root, id);
    if(!path) return false;

    const last = path[path.length - 1];
    const target = last.block.children[last.index];
    const own = target.kind === "stick" ? findOwnEdgeStick(root, id) : null;

    // a block's own edge stick stays on the outline: the cell it abuts goes instead,
    // so the frame collapses inward onto the next cell.
    let cellPath = path;
    if(own) {
        const cellIdx = sideIsLeading(own.side) ? last.index + 1 : last.index - 1;
        if(!isRemovableCell(last.block.children[cellIdx])) return false;
        cellPath = [...path.slice(0, -1), {block: last.block, index: cellIdx}];
    } else if(target.kind === "stick") {
        const cellIdx = isRemovableCell(last.block.children[last.index + 1]) ? last.index + 1 : last.index - 1;
        if(!isRemovableCell(last.block.children[cellIdx])) return false;
        cellPath = [...path.slice(0, -1), {block: last.block, index: cellIdx}];
    }
    const covers = outlineCovers(spec, cellPath);

    for(let depth = path.length - 1; depth >= 0; depth--) {
        const step = path[depth];
        const child = step.block.children[step.index];

        if(depth === path.length - 1 && child.kind === "stick" && !own) {
            if(!removeStickWithNeighbour(step.block, step.index)) return false;
        } else {
            removeCellAt(step.block, cellPath[depth].index);
        }

        // parent still holds cells, or we've reached the root we can't remove.
        if(!isBlockEmpty(step.block) || depth === 0) break;
        // otherwise the next (shallower) step removes this now-empty block from
        // its own parent — that step's index is unaffected by our edit above.
    }

    collapseWrappers(spec);
    restoreOutlineCover(spec, covers);
    return true;
}

// the stick covering each side of the frame outline at the cell at the end of
// `path` (sides the cell doesn't reach are left out).
function outlineCovers(spec: MdFrameSpec, path: PathStep[]): Map<OutlineSide, MdFrameStick> {
    const out = new Map<OutlineSide, MdFrameStick>();
    for(const side of ["top", "bottom", "left", "right"] as OutlineSide[]) {
        const cover = outlineCover(spec, path, side);
        if(cover) out.set(side, cover);
    }
    return out;
}

// put a stick back on each side of the frame outline a removal left open: the
// leaves on `side` with no stick covering them (the door's sill and a void aside)
// take the stick that covered the removed cell there, so the frame stays closed.
function restoreOutlineCover(spec: MdFrameSpec, covers: Map<OutlineSide, MdFrameStick>) {
    const root = spec.frameBlocks;
    const sticks = sticksById(root);

    for(const [side, stick] of covers) {
        const open = edgeLeafIds({kind: "block", block: root}, side).filter(id => {
            const path = findItemPath(root, id);
            if(!path) return false;
            const last = path[path.length - 1];
            const cell = last.block.children[last.index];
            if(cell.kind !== "item" || cell.item.kind === "void") return false;
            if(side === "bottom" && (cell.item.kind === "doors" || cell.item.kind === "threshold")) return false;
            return outlineCover(spec, path, side) === null;
        });
        if(open.length === 0) continue;

        const runs = groupLeafRuns(root, open, side);
        if(!runs) continue;

        // the stick is back in the tree on its own unless the stick it was joined to
        // is still there.
        if(stick.joinedTo && !sticks.has(stick.joinedTo)) stick.joinedTo = undefined;
        giveRunsSticks(spec, runs, side, stick);
    }
}

// convert a lite <-> fixedPanel in place. Sizing is identical between the two
// kinds, so only the kind (and its fill configuration) flips; the grid view
// reconciles the face on the next frameBlocksChanged. Returns false when the id
// isn't a (non-door) item.
export function convertItem(spec: MdFrameSpec, itemId: string, toKind: "lite" | "fixedPanel"): boolean {
    const path = findItemPath(spec.frameBlocks, itemId);
    if(!path) return false;

    const step = path[path.length - 1];
    const ref = step.block.children[step.index];
    if(ref.kind !== "item" || ref.item.kind === "doors") return false;

    // the outgoing fill's configuration means nothing to the new kind, so clear it
    // (mutated in place — the grid view holds this object by reference). The mark goes
    // with it: a cell that isn't glass any more releases its number, and converting back
    // takes a fresh one from assignLiteMarks.
    const wasForced = forcedProfile(ref.item);
    Object.assign(ref.item, {kind: toKind, mark: undefined, glazing: undefined, glass: undefined, material: undefined, position: undefined});

    // the new kind starts on its defaults, which force nothing — so withdraw whatever
    // the outgoing configuration had stamped.
    applyForcedProfile(spec, itemId, undefined, wasForced);
    return true;
}

// the item with this id, or undefined when the id isn't an item.
export function findItem(spec: MdFrameSpec, itemId: string): MdFrameItem | undefined {
    const path = findItemPath(spec.frameBlocks, itemId);
    if(!path) return undefined;
    const step = path[path.length - 1];
    const ref = step.block.children[step.index];
    return ref.kind === "item" ? ref.item : undefined;
}

// set a lite's glazing, then re-apply the frame profile the new glazing forces
// (withdrawing the outgoing one when it forces none). Returns false when the id isn't
// a lite.
export function setCellGlazing(spec: MdFrameSpec, itemId: string, glazing: MdGlazing): boolean {
    const item = findItem(spec, itemId);
    if(item?.kind !== "lite") return false;
    const was = forcedProfile(item);
    item.glazing = glazing;
    applyForcedProfile(spec, itemId, forcedProfile(item), was);
    return true;
}

// set a lite's glass configuration. Returns false when the id isn't a lite.
export function setCellGlass(spec: MdFrameSpec, itemId: string, glass: MdGlass | undefined): boolean {
    const item = findItem(spec, itemId);
    if(item?.kind !== "lite") return false;
    item.glass = glass;
    return true;
}

// set a fixed panel's material. Returns false when the id isn't a fixed panel.
export function setCellPanelMaterial(spec: MdFrameSpec, itemId: string, material: MdPanelMaterial): boolean {
    const item = findItem(spec, itemId);
    if(item?.kind !== "fixedPanel") return false;
    item.material = material;
    return true;
}

// set a fixed panel's position, then re-apply the frame profile the new position
// forces (withdrawing the outgoing one when it forces none). Returns false when the
// id isn't a fixed panel.
export function setCellPanelPosition(spec: MdFrameSpec, itemId: string, position: MdPanelPosition): boolean {
    const item = findItem(spec, itemId);
    if(item?.kind !== "fixedPanel") return false;
    const was = forcedProfile(item);
    item.position = position;
    applyForcedProfile(spec, itemId, forcedProfile(item), was);
    return true;
}

// point a cell and the sticks bounding it at the frame profile its fill configuration
// requires. `profile` undefined means the new configuration forces none: then only the
// profile `replacing` stamped is withdrawn, so a profile the user picked by hand
// survives a glazing change that has no opinion about it.
export function applyForcedProfile(
    spec: MdFrameSpec,
    itemId: string,
    profile: MdFrameProfileType | undefined,
    replacing?: MdFrameProfileType,
): boolean {
    if(!profile && !replacing) return false;

    let changed = false;
    if(profile || getItemProfile(spec, itemId) === replacing) {
        changed = setItemProfile(spec, itemId, profile);
    }

    for(const b of cellBorderSticks(spec, itemId)) {
        if(!profile && getStickProfile(spec, b.stick.id, b.face) !== replacing) continue;
        if(setStickProfile(spec, b.stick.id, b.face, profile)) changed = true;
    }

    return changed;
}

// the vertical sticks bounding a cell, each with the face that points at the cell:
// the stick on the cell's left presents its right face, and vice versa.
export function cellBorderSticks(spec: MdFrameSpec, itemId: string): {stick: MdFrameStick; face: "left" | "right"}[] {
    const path = findItemPath(spec.frameBlocks, itemId);
    if(!path) return [];

    const out: {stick: MdFrameStick; face: "left" | "right"}[] = [];
    for(const side of ["left", "right"] as const) {
        const stick = borderStick(path, side);
        if(stick) out.push({stick, face: side === "left" ? "right" : "left"});
    }
    return out;
}

// the first stick outward from the cell on `side`: the mullion or jamb beside it
// within a row, else the same search against that block's own position one level up.
// Undefined when the neighbour is another cell (no separator) or nothing bounds that
// side.
function borderStick(path: PathStep[], side: "left" | "right"): MdFrameStick | undefined {
    for(let depth = path.length - 1; depth >= 0; depth--) {
        const step = path[depth];

        if(step.block.kind === "row") {
            const neighbour = step.block.children[side === "left" ? step.index - 1 : step.index + 1];
            if(neighbour) return neighbour.kind === "stick" ? neighbour.stick : undefined;
        }
    }

    return undefined;
}

// read an item's per-item frame-profile override (undefined = inherit the spec
// default). undefined too when the id isn't an item.
export function getItemProfile(spec: MdFrameSpec, itemId: string): MdFrameProfileType | undefined {
    const path = findItemPath(spec.frameBlocks, itemId);
    if(!path) return undefined;
    const step = path[path.length - 1];
    const ref = step.block.children[step.index];
    return ref.kind === "item" ? ref.item.profile : undefined;
}

// override an item's frame profile; pass undefined to clear the override and inherit
// the spec default. Doors always follow the spec, so they're rejected. Returns false
// when the id isn't a settable (non-door) item.
export function setItemProfile(spec: MdFrameSpec, itemId: string, profile: MdFrameProfileType | undefined): boolean {
    const path = findItemPath(spec.frameBlocks, itemId);
    if(!path) return false;
    const step = path[path.length - 1];
    const ref = step.block.children[step.index];
    if(ref.kind !== "item" || ref.item.kind === "doors") return false;
    ref.item.profile = profile;
    return true;
}

// The mullion stick + neighbouring cell on `side` of the door, when that neighbour
// is a lite/panel across a single mullion in the door's row — the case the frame
// profile view draws as a double-sided (clamshell) mullion. Returns null for a
// perimeter jamb, a nested / non-row parent, or a non-lite neighbour.
// true when `block` is the door cell: a column stacking the doors on the threshold
// (its sill), with no other cell. Its edge sticks, if any, don't count.
export function isDoorCell(block: MdFrameBlock): boolean {
    if(block.kind !== "column") return false;
    const cells = block.children.filter(isCell);
    return cells.length === 2
        && cells[0].kind === "item" && cells[0].item.kind === "doors"
        && cells[1].kind === "item" && cells[1].item.kind === "threshold";
}

// the door cell's place in its row: the row and the door cell's index in it, or null
// when the doors item isn't in a door cell inside a row.
function doorCellStep(spec: MdFrameSpec): PathStep | null {
    const primary = primaryDoorsItem(spec);
    if(!primary) return null;

    const path = findItemPath(spec.frameBlocks, primary.id);
    if(!path || path.length < 2) return null;

    const cellStep = path[path.length - 1];
    if(!isDoorCell(cellStep.block)) return null;

    const rowStep = path[path.length - 2];
    if(rowStep.block.kind !== "row") return null;
    return rowStep;
}

// the top-level row the door sits in (the horizontal run of cells the plan/top-down
// section spans), or null when that row isn't the frame's main run — e.g. it sits in
// a nested block under a transom, where a single horizontal section can't represent
// the whole run. The main run is the root, or the root's only cell (the root then
// holds the head and sill). Callers fall back to the simple jamb/door/jamb section.
export function doorTopLevelRow(spec: MdFrameSpec): MdFrameBlock | null {
    const rowStep = doorCellStep(spec);
    if(!rowStep) return null;

    const row = rowStep.block;
    const root = spec.frameBlocks;
    if(row === root) return row;

    const rootCells = root.children.filter(isCell);
    if(rootCells.length !== 1 || rootCells[0].kind !== "block" || rootCells[0].block !== row) return null;
    return row;
}

export function doorSideNeighbour(spec: MdFrameSpec, side: "left" | "right"): {stick: MdFrameStick; item: MdFrameItem} | null {
    const step = doorCellStep(spec);
    if(!step) return null;
    const parent = step.block;

    // children alternate cell / stick / cell; step one over the mullion to the cell.
    const stickIdx = side === "left" ? step.index - 1 : step.index + 1;
    const cellIdx = side === "left" ? step.index - 2 : step.index + 2;

    const stickChild = parent.children[stickIdx];
    const cellChild = parent.children[cellIdx];
    if(!stickChild || stickChild.kind !== "stick") return null;
    if(!cellChild || cellChild.kind !== "item") return null;
    if(cellChild.item.kind !== "lite" && cellChild.item.kind !== "fixedPanel") return null;

    return {stick: stickChild.stick, item: cellChild.item};
}

// ids come from the spec's saved counter so they stay unique after the design is reopened
function nextId(spec: MdFrameSpec, prefix: string): string {
    return `${prefix}:${spec.frameBlockId++}`;
}

// the two model nodes a split/insert always adds: a fresh default mullion and the
// lite cell it separates. Returned with narrow kinds so callers can read .stick /
// .item without re-narrowing.
function newMullion(spec: MdFrameSpec): {kind: "stick"; stick: MdFrameStick} {
    return {kind: "stick", stick: {id: nextId(spec, "mullion")}};
}

// create a new lite cell. `sizeInches` seeds its flow-axis size; pinned (the
// default) so the solver honours it and the frame grows to fit, or soft so the
// solver may refine it (e.g. splitItem sharing a footprint). Defaults to NEW_LITE_SIZE.
function newLiteCell(spec: MdFrameSpec, sizeInches: number = NEW_LITE_SIZE, pin: boolean = true): {kind: "item"; item: MdFrameItem} {
    const id = nextId(spec, "lite");
    let size: FrmVar;
    if(pin) {
        // pin it (isModified) so it's a hard dimension the solver keeps — an unpinned
        // seed collapses to the solver's min when the frame resists growing. Start
        // from 0 so userSet sees a changed value and actually flips isModified (it
        // no-ops when the value already matches).
        size = new FrmVar(id + ":size", 0);
        size.userSet(sizeInches, {debug: "new-lite-default-size"});
    } else {
        size = new FrmVar(id + ":size", sizeInches);
    }
    return {kind: "item", item: {id, kind: "lite", size}};
}

// split a cell in two by adding a sibling lite (and the mullion between them). A
// "vertical" split places the halves side-by-side (a row); "horizontal" stacks
// them (a column). When the cell's parent already runs the needed direction we
// insert into it; otherwise we wrap the cell in a new block of that direction.
// `current` is the cell's resolved on-screen size, used to size the halves so the
// split stays within the original footprint.
export function splitItem(spec: MdFrameSpec, itemId: string, direction: "vertical" | "horizontal", current: {width: number; height: number}): boolean {
    const path = findItemPath(spec.frameBlocks, itemId);
    if(!path) return false;

    const step = path[path.length - 1];
    const parent = step.block;
    const idx = step.index;

    const childRef = parent.children[idx];
    if(childRef.kind !== "item") return false;
    const item = childRef.item;

    const needKind: "row" | "column" = direction === "vertical" ? "row" : "column";
    const newStick = newMullion(spec);

    // the halves split the cell's extent along the split direction (less the new
    // mullion) evenly: the pair occupies exactly the cell's old footprint, so the
    // siblings and the overall frame don't shift. Both are soft (unpinned), so the
    // solver may still refine them — but they start even rather than at the default
    // lite size.
    const splitExtent = needKind === "row" ? current.width : current.height;
    const half = Math.max((splitExtent - stickThicknessVar(newStick.stick, spec).get()) / 2, 0);
    const newLite = newLiteCell(spec, half, false);

    if(parent.kind === needKind) {
        // the split runs along the parent's main axis, which is shared (additively)
        // with the sibling cells.
        if(item.kind !== "doors") {
            if(!item.size) item.size = new FrmVar(nextId(spec, "split-size"), half);
            else item.size.set(half, { debug: "split-item" });
        }

        parent.children.splice(idx + 1, 0, newStick, newLite);
        return true;
    }

    // wrap the cell in a new block running the needed direction. The cell's
    // along-parent-axis size moves to the wrapper so the footprint is preserved;
    // the two halves then share that footprint (the wrapper's cross axis).
    // the wrapper occupies the cell's old slot, so its size axis (the parent's
    // flow) should preserve the cell's along-parent extent. When the cell carried
    // its own size we move it up; otherwise seed a fresh (soft) size from the
    // resolved footprint so the solver starts from the same place.
    const alongParent = parent.kind === "row" ? current.width : current.height;
    const wrapper: MdFrameBlock = {
        id: nextId(spec, "block"),
        kind: needKind,
        size: new FrmVar(nextId(spec, "block-size"), alongParent),
        children: [childRef, newStick, newLite],
    };

    if(item.kind !== "doors") {
        if(item.size) {
            wrapper.size = item.size;
            // the var now drives the wrapper block's size, not the leaf's — relabel it so
            // the debug panel names it for its new owner instead of keeping the leaf's
            // stale `<leaf>:size` name (which then collides with the fresh size the leaf
            // materializes on rebuild).
            wrapper.size.rename(wrapper.id + ":size");
        }
        item.size = new FrmVar(nextId(spec, "split-size"), half);
    }

    parent.children[idx] = {
        kind: "block",
        block: wrapper
    };

    return true;
}

// which side a clicked stick offers an insert on. A vertical mullion (in a row)
// offers left/right; a horizontal mullion (in a column) offers above/below.
export type StickInsertSide = "left" | "right" | "above" | "below";

// where a perimeter stick lives in the model: the block that owns the perimeter,
// the side it's on, and (for a split bottom) which of the three pieces it is.
export type PerimeterLocation = {
    block: MdFrameBlock;
    side: "top" | "bottom" | "left" | "right";
    role: "single" | "left" | "threshold" | "right";
    stick: MdFrameStick;
};

// locate a perimeter stick by id within the model. The outline sticks now live in the
// tree as a block's own edge sticks (see inline-outline-plan.md), so no stick is a
// perimeter stick any more: always null. Phase 2 removes this and the branches on it.
export function findPerimeterStick(root: MdFrameBlock, stickId: string): PerimeterLocation | null {
    return null;
}

export function isPerimeterStick(spec: MdFrameSpec, stickId: string): boolean {
    return findPerimeterStick(spec.frameBlocks, stickId) !== null;
}

// the block whose flow runs across `perimSide`, so the edge abuts its leading/
// trailing cell. Usually the perimeter's own block; when the edge runs along that
// block's flow (a row's head/sill), descend through single-cell wrappers (the
// shape insertAcrossPerimeter leaves) to the block stacking against the edge.
// Null when the edge spans several cells (ambiguous) or no such block exists.
function perimeterEdgeBlock(block: MdFrameBlock, perimSide: string): MdFrameBlock | null {
    for(;;) {
        if(inwardPerimeterInsert(block, perimSide)) return block;

        const cells = block.children.filter(isCell);
        if(cells.length !== 1 || cells[0].kind !== "block") return null;
        block = cells[0].block;
    }
}

// the child index of the cell a perimeter stick abuts inward (the block's leading/
// trailing cell, stepping over an edge-override stick like the threshold).
function perimeterEdgeCellIdx(block: MdFrameBlock, perimSide: string): number | null {
    const inward = inwardPerimeterInsert(block, perimSide);
    if(!inward) return null;

    const children = block.children;
    if(inward.atStart) {
        const idx = children[0]?.kind === "stick" ? 1 : 0;
        return isCell(children[idx]) ? idx : null;
    }
    const last = children.length - 1;
    const idx = children[last]?.kind === "stick" ? last - 1 : last;
    return isCell(children[idx]) ? idx : null;
}

// how removing a perimeter stick collapses the frame inward: the edge cell it
// abuts gets dropped (with its mullion) and the border moves against the next
// cell. Allowed only when that edge cell is a removable lite/panel and at least
// one other cell survives; null otherwise (including non-perimeter ids).
function perimeterRemoval(spec: MdFrameSpec, stickId: string): {block: MdFrameBlock; cellIdx: number} | null {
    const loc = findPerimeterStick(spec.frameBlocks, stickId);
    if(!loc) return null;

    const block = perimeterEdgeBlock(loc.block, loc.side);
    if(!block) return null;

    const idx = perimeterEdgeCellIdx(block, loc.side);
    if(idx === null) return null;
    if(!isRemovableCell(block.children[idx])) return null;
    if(block.children.filter(isCell).length < 2) return null;
    return {block, cellIdx: idx};
}

// whether a stick offers a remove action:
//   - an interior mullion: needs a removable lite/panel cell beside it to drop
//     along with it (a door neighbour on one side is fine — the other side goes).
//   - a perimeter border: removable when it can collapse inward (see
//     perimeterRemoval).
export function canRemoveStick(spec: MdFrameSpec, stickId: string): boolean {
    if(isPerimeterStick(spec, stickId)) return perimeterRemoval(spec, stickId) !== null;

    const path = findStickPath(spec.frameBlocks, stickId);
    if(!path) return false;

    // a block's own edge stick collapses the frame inward: the cell it abuts goes.
    const own = findOwnEdgeStick(spec.frameBlocks, stickId);
    if(own) return isRemovableCell(path.block.children[sideIsLeading(own.side) ? path.index + 1 : path.index - 1]);

    return isRemovableCell(path.block.children[path.index - 1])
        || isRemovableCell(path.block.children[path.index + 1]);
}

// the block holding a separator stick and that stick's index in its children.
// Returns null for sticks that aren't a `kind: "stick"` child — e.g. the synthetic
// perimeter sticks, whose ids are handled separately.
function findStickPath(root: MdFrameBlock, stickId: string): {block: MdFrameBlock; index: number} | null {
    for(let i = 0; i < root.children.length; i++) {
        const c = root.children[i];
        if(c.kind === "stick" && c.stick.id === stickId) {
            return {block: root, index: i};
        }
        if(c.kind === "block") {
            const sub = findStickPath(c.block, stickId);
            if(sub) return sub;
        }
    }
    return null;
}

function isCell(c: MdFrameBlockChild | undefined): boolean {
    return !!c && (c.kind === "item" || c.kind === "block");
}

// one editable profile face of a stick, with its picker caption. `face` is the model
// side (the face pointing left/−x or right/+x); `label` is what the ribbon shows.
export type StickProfileFace = {face: "left" | "right"; label: string};

// the profile faces a stick exposes for editing in the top-down section, with UI
// labels. An interior vertical mullion faces a cell on each side (Left / Right); a
// vertical perimeter jamb faces a cell only on its inner side (a single "Profile" —
// the left jamb's inner face points right, the right jamb's points left). Horizontal
// sticks (perimeter top/bottom, mullions inside a column) and the threshold don't
// appear in the top-down section, so they return [].
export function getStickProfileFaces(spec: MdFrameSpec, stickId: string): StickProfileFace[] {
    const peri = findPerimeterStick(spec.frameBlocks, stickId);
    if(peri) {
        if(peri.side === "left") return [{face: "right", label: "Profile"}];
        if(peri.side === "right") return [{face: "left", label: "Profile"}];
        return [];
    }

    const path = findStickPath(spec.frameBlocks, stickId);
    if(!path) return [];

    // an air gap has no stock to profile.
    if(isAirGap(path.block.children[path.index])) return [];

    // a stick child inside a row is a vertical mullion; one inside a column is
    // horizontal (out of scope for the top-down section).
    if(path.block.kind !== "row") return [];
    if(!isCell(path.block.children[path.index - 1]) || !isCell(path.block.children[path.index + 1])) return [];
    return [{face: "left", label: "Left"}, {face: "right", label: "Right"}];
}

// the stick with this id, whether an interior mullion (a `kind:"stick"` child) or a
// perimeter jamb (on a block's `perimeter`). Undefined when no stick matches.
export function findStickById(spec: MdFrameSpec, stickId: string): MdFrameStick | undefined {
    const peri = findPerimeterStick(spec.frameBlocks, stickId);
    if(peri) return peri.stick;

    const path = findStickPath(spec.frameBlocks, stickId);
    if(!path) return undefined;
    const c = path.block.children[path.index];
    return c.kind === "stick" ? c.stick : undefined;
}

// read a stick's per-face frame-profile override (undefined = inherit the neighbouring
// cell's profile, then the spec default).
export function getStickProfile(spec: MdFrameSpec, stickId: string, face: "left" | "right"): MdFrameProfileType | undefined {
    const stick = findStickById(spec, stickId);
    if(!stick) return undefined;
    return face === "left" ? stick.profileLeft : stick.profileRight;
}

// override a stick's frame profile on one face; pass undefined to clear the override.
// Returns false when the id isn't a stick.
export function setStickProfile(spec: MdFrameSpec, stickId: string, face: "left" | "right", profile: MdFrameProfileType | undefined): boolean {
    const stick = findStickById(spec, stickId);
    if(!stick) return false;
    if(face === "left") stick.profileLeft = profile;
    else stick.profileRight = profile;
    return true;
}

// left/right grow a row's flow; above/below grow a column's.
function sideKind(side: StickInsertSide): "row" | "column" {
    return side === "left" || side === "right" ? "row" : "column";
}

// whether the new cell lands after the reference stick (toward a higher index).
function sideIsAfter(side: StickInsertSide): boolean {
    return side === "right" || side === "below";
}

// the edge a perimeter cell inserts against (atStart = leading edge), or null when
// the edge runs *along* the block's flow (so it has no leading/trailing cell slot —
// e.g. the top/bottom of a row). A perimeter edge only ever has an inward side, so
// we ignore which button was pressed and always insert there.
function inwardPerimeterInsert(block: MdFrameBlock, perimSide: string): {atStart: boolean} | null {
    if(block.kind === "row") {
        if(perimSide === "left") return {atStart: true};
        if(perimSide === "right") return {atStart: false};
        return null;
    }
    if(perimSide === "top") return {atStart: true};
    if(perimSide === "bottom") return {atStart: false};
    return null;
}

// insert a lite + mullion at a block's leading or trailing edge, stepping over an
// edge-override stick (e.g. a threshold) so the new cell stays inside the frame.
// Returns the new lite's id.
function insertAtBlockEdge(spec: MdFrameSpec, block: MdFrameBlock, atStart: boolean): string {
    const children = block.children;
    const lite = newLiteCell(spec);
    if(atStart) {
        const idx = children[0]?.kind === "stick" ? 1 : 0;
        children.splice(idx, 0, lite, newMullion(spec));
    } else {
        const last = children.length - 1;
        const idx = children[last]?.kind === "stick" ? last : children.length;
        children.splice(idx, 0, newMullion(spec), lite);
    }
    return lite.item.id;
}

// add a new lite cell (and the mullion joining it) beside a stick, growing the
// containing block — the new lite is left unsized so the layout solver gives it a
// default extent and the frame grows to fit (siblings keep their sizes).
//
// `stickId` is the clicked StickMaterial's id and `side` the button pressed. We
// insert wherever there's actually a slot rather than forcing the requested side:
//   - interior mullion: insert on the requested side, falling back to the other
//     side if that one butts against an edge.
//   - perimeter stick (outer frame): insert inward at the owning block's leading /
//     trailing edge. When that edge runs along the block's flow (a row's head/sill)
//     it stacks a cell into a containing block of the opposite orientation instead,
//     creating one at the root if none exists (see insertAcrossPerimeter).
// Returns false only when the stick can't be located in the model.
export function insertBesideStick(spec: MdFrameSpec, stickId: string, side: StickInsertSide): boolean {
    const root = spec.frameBlocks;

    // perimeter sticks live on a block's `perimeter` — locate via the model.
    const perim = findPerimeterStick(root, stickId);
    if(perim) {
        // the new cell takes the whole edge on this side, so the sticks split off it
        // would end up inside the frame. A step there can't move inside the frame.
        const side = perim.side as OutlineSide;
        const owners = outlineEdgeOwners(root, side);
        if(owners.some(o => !ownEdgeStick(o, side))) return false;
        for(const id of owners.map(o => ownEdgeStick(o, side)!.id)) {
            const owner = findEdgeOwner(spec, id);
            if(owner) dropOwnEdgeStick(owner);
        }

        // an edge that runs along the block's flow (a row's head/sill, a column's side
        // jamb) has no in-flow slot — stacking a cell there needs a container of the
        // opposite orientation. Find one above, or wrap the root in one.
        const inward = inwardPerimeterInsert(perim.block, perim.side);
        const liteId = inward
            ? insertAtBlockEdge(spec, perim.block, inward.atStart)
            : insertAcrossPerimeter(spec, perim.block, perim.side);

        coverNewCell(spec, liteId);
        return true;
    }

    const path = findStickPath(root, stickId);
    if(!path) return false;

    const {block, index} = path;
    // the offered sides come from the stick's orientation, which must match the
    // block's flow direction (a vertical mullion lives in a row, etc.).
    if(block.kind !== sideKind(side)) return false;

    // prefer the requested side; fall back to the other when it butts against an
    // edge with no cell to sit beside (the stick is the block's edge stick, so the
    // new cell goes inside it). Either way, insert on the side that works.
    const after = sideIsAfter(side);
    let liteId: string | null = null;
    if(isCell(block.children[after ? index + 1 : index - 1])) {
        liteId = insertIntoChildren(spec, block, index, after);
    } else if(isCell(block.children[after ? index - 1 : index + 1])) {
        liteId = insertIntoChildren(spec, block, index, !after);
    }
    if(!liteId) return false;

    coverNewCell(spec, liteId);
    return true;
}

// splice a lite + mullion beside the stick at `index`, keeping the new lite on the
// far side of the new mullion from the stick so the cell/stick alternation holds.
// Returns the new lite's id.
function insertIntoChildren(spec: MdFrameSpec, block: MdFrameBlock, index: number, after: boolean): string {
    const lite = newLiteCell(spec);
    if(after) {
        block.children.splice(index + 1, 0, lite, newMullion(spec));
    } else {
        block.children.splice(index, 0, newMullion(spec), lite);
    }
    return lite.item.id;
}

// stack a new cell across a perimeter edge that runs along the owning block's flow
// (a row's head/sill, a column's side jamb). The new cell can't live in `block` —
// that block flows the wrong way — so it goes into a containing block of the
// opposite orientation: the nearest such ancestor, or a new root wrapping the whole
// tree when none exists. The edge picks which end the cell lands on (top/left lead).
// Returns the new lite's id.
function insertAcrossPerimeter(spec: MdFrameSpec, block: MdFrameBlock, perimSide: string): string {
    const needKind: "row" | "column" = block.kind === "row" ? "column" : "row";
    const before = perimSide === "top" || perimSide === "left";

    // climb to the nearest container of the needed orientation and add the cell
    // beside the child on the path (a sibling of `block` at that level).
    const ancestor = findAncestorOfKind(spec.frameBlocks, block.id, needKind);
    if(ancestor) {
        return insertBesideCell(spec, ancestor.container, ancestor.childIndex, before);
    }

    // nothing above — look just below: descend through single-cell wrappers (the
    // shape the root wrap here builds) and stack into the first block of the needed
    // orientation, so repeated inserts on the same edge reuse one container instead
    // of wrapping the tree in a new column + row pair each time.
    let inner: MdFrameBlock = block;
    for(;;) {
        if(inner.children.length !== 1) break;
        const only = inner.children[0];
        if(only.kind !== "block") break;
        inner = only.block;
        if(inner.kind === needKind) {
            return insertAtBlockEdge(spec, inner, before);
        }
    }

    // nothing of that orientation above or below: wrap the whole tree in a new
    // block of needKind and drop the existing root in beside the new cell + mullion.
    const oldRoot = spec.frameBlocks;
    const rootChild: MdFrameBlockChild = {kind: "block", block: oldRoot};
    const newStick = newMullion(spec);
    const newCell = newLiteCell(spec);

    setRootBlock(spec, {
        id: nextId(spec, needKind),
        kind: needKind,
        size: new FrmVar(nextId(spec, "block-size"), 0),
        children: before ? [newCell, newStick, rootChild] : [rootChild, newStick, newCell],
    });

    return newCell.item.id;
}

// install `block` as the model root, enforcing the invariant that the root is
// always a row. A column root is wrapped in a row (stick, column, stick) so the
// outer block stays a row while the column carries the vertical stacking.
function setRootBlock(spec: MdFrameSpec, block: MdFrameBlock) {
    if(block.kind === "row") {
        spec.frameBlocks = block;
        return;
    }

    spec.frameBlocks = {
        id: nextId(spec, "row"),
        kind: "row",
        size: new FrmVar(nextId(spec, "block-size"), 0),
        children: [{kind: "block", block}],
    };
}

// splice a lite + mullion beside the cell at `cellIndex`, mirroring insertIntoChildren
// but anchored on a cell instead of a stick (used when inserting a sibling into a
// containing row/column found by traversal). Returns the new lite's id.
function insertBesideCell(spec: MdFrameSpec, block: MdFrameBlock, cellIndex: number, before: boolean): string {
    const lite = newLiteCell(spec);
    if(before) {
        block.children.splice(cellIndex, 0, lite, newMullion(spec));
    } else {
        block.children.splice(cellIndex + 1, 0, newMullion(spec), lite);
    }
    return lite.item.id;
}

// a cell added on the frame outline is covered on each side it reaches: either a stick
// of an enclosing block already covers it (the head over a row), or it gets a stick of
// its own there. That stick continues the edge of the cell beside it: when that cell's
// edge has a stick (Split By, a step), the new stick copies it and is joined to it (with
// a step of the same size), so the two draw as one. With no edge stick beside it (the
// door's threshold), a fresh default stick.
function coverNewCell(spec: MdFrameSpec, itemId: string) {
    const root = spec.frameBlocks;
    for(const side of ["top", "bottom", "left", "right"] as OutlineSide[]) {
        const path = findItemPath(root, itemId);
        if(!path) return;
        if(!reachesSide(path, side)) continue;
        if(coverAround(spec, path, side)) continue;

        const order = edgeLeafIds({kind: "block", block: root}, side);
        const pos = order.indexOf(itemId);
        const beside = [order[pos - 1], order[pos + 1]].filter((id): id is string => id !== undefined);
        const edge = beside
            .map(id => {
                const p = findItemPath(root, id);
                return p ? edgeStickAt(p, side) : null;
            })
            .find(e => !!e) ?? null;

        const stick: MdFrameStick = edge ? newStickLike(spec, edge) : {id: nextId(spec, "stick")};
        if(edge) stick.joinedTo = joinedRoot(spec, edge.id);

        const at = path[path.length - 1];
        let owner = at.block;
        if(owner.kind === edgeOwnerKind(side)) {
            if(sideIsLeading(side)) owner.children.unshift({kind: "stick", stick});
            else owner.children.push({kind: "stick", stick});
        } else {
            owner = giveOwnEdgeStick(spec, at.block, at.index, at.index, side, stick);
        }

        if(!edge) continue;
        const step = stepVoids(root).get(edge.id);
        if(step?.kind !== "void") continue;
        const cell: MdFrameBlockChild = {kind: "item", item: {id: nextId(spec, "void"), kind: "void", size: step.size}};
        if(sideIsLeading(side)) owner.children.unshift(cell);
        else owner.children.push(cell);
    }
}

// true when the cell at the end of `path` touches `side` of the frame outline.
function reachesSide(path: PathStep[], side: OutlineSide): boolean {
    return path.every(step => cellIndexesOnSide(step.block, side).includes(step.index));
}

// the blocks below `root` on `side` of the frame outline that have a stick of their own
// there (an own edge stick or a step stick).
function outlineEdgeOwners(root: MdFrameBlock, side: OutlineSide): MdFrameBlock[] {
    const out: MdFrameBlock[] = [];
    const walk = (block: MdFrameBlock) => {
        for(const i of cellIndexesOnSide(block, side)) {
            const c = block.children[i];
            if(c.kind !== "block") continue;
            if(edgeOrStepStick(c.block, side)) out.push(c.block);
            walk(c.block);
        }
    };
    walk(root);
    return out;
}

// the stick on `side` of the frame outline at the leaf cell at the end of `path`: the
// own edge stick or step stick of the deepest block above it that has one there. Null
// when the perimeter covers it, or the cell doesn't reach that side.
function edgeStickAt(path: PathStep[], side: OutlineSide): MdFrameStick | null {
    let out: MdFrameStick | null = null;
    for(const step of path) {
        if(!cellIndexesOnSide(step.block, side).includes(step.index)) return null;
        out = edgeOrStepStick(step.block, side) ?? out;
    }
    return out;
}

// the steps from `root` down to the block with `targetId`: each step descends into
// step.block.children[step.index]; the final step's child is the target. Empty when
// the target is the root itself, null when it isn't found.
function findBlockPath(root: MdFrameBlock, targetId: string): {block: MdFrameBlock; index: number}[] | null {
    if(root.id === targetId) return [];
    for(let i = 0; i < root.children.length; i++) {
        const c = root.children[i];
        if(c.kind === "block") {
            if(c.block.id === targetId) return [{block: root, index: i}];
            const sub = findBlockPath(c.block, targetId);
            if(sub) return [{block: root, index: i}, ...sub];
        }
    }
    return null;
}

// the path to a cell: an item, or a block (see findItemPath, findBlockPath).
function findCellPath(root: MdFrameBlock, cellId: string): PathStep[] | null {
    return findItemPath(root, cellId) ?? findBlockPath(root, cellId);
}

// the nearest ancestor of `targetId` whose kind is `kind`, plus the index (within
// that ancestor's children) of the descendant on the path. Null when there's no
// such ancestor (the target is at or above that kind in the tree).
function findAncestorOfKind(root: MdFrameBlock, targetId: string, kind: "row" | "column"): {container: MdFrameBlock; childIndex: number} | null {
    const path = findBlockPath(root, targetId);
    if(!path) return null;

    for(let i = path.length - 1; i >= 0; i--) {
        if(path[i].block.kind === kind) {
            return {container: path[i].block, childIndex: path[i].index};
        }
    }
    return null;
}

// ---- outline corner joints --------------------------------------------------
// A corner is where a horizontal and a vertical stick piece of the frame outline meet
// end to end (see Terminations.detectCorners, which records them in
// spec.cornerInfo). Editing it is non-structural: it just writes terminateA/B on the
// two model sticks, which the termination resolver reads back at render. The two
// sticks are kept complementary (one extends / owns the corner, the other caps) so
// reading either end stays consistent; a miter sets both to "miter".

type CornerAxis = "h" | "v";

// the stick a stick draws as: the stick it was joined into (see joinSticks), or itself.
export function drawnStick(spec: MdFrameSpec, stick: MdFrameStick): MdFrameStick {
    if(!stick.joinedTo) return stick;
    const sticks = sticksById(spec.frameBlocks);
    return sticks.get(joinedRootId(sticks, stick.id)) ?? stick;
}

// the model stick id of a drawn piece's selection id: a piece the renderer split off a
// stick carries "#n" after the stick's id (see InteriorStick.makePart).
export function pieceStickId(pieceId: string): string {
    const at = pieceId.indexOf("#");
    return at < 0 ? pieceId : pieceId.slice(0, at);
}

function parseCornerKey(key: string): {corner: string; axis: CornerAxis} {
    const at = key.lastIndexOf("#");
    return {corner: key.slice(0, at), axis: key.slice(at + 1) as CornerAxis};
}

function endJoint(stick: MdFrameStick | undefined, end: "A" | "B"): MdFrameJoint | undefined {
    if(!stick) return undefined;
    return end === "A" ? stick.terminateA : stick.terminateB;
}

function setEndJoint(stick: MdFrameStick, end: "A" | "B", joint: MdFrameJoint) {
    if(end === "A") stick.terminateA = joint;
    else stick.terminateB = joint;
}

// the corners a selected stick touches, each as a key + label + whether a miter is
// allowed there (corners including a threshold can't be mitered) + whether it is
// locked (a floor corner, or an end the renderer split off the model stick).
export function stickCorners(spec: MdFrameSpec, stickId: string): {key: string; label: string; allowMiter: boolean; locked: boolean}[] {
    const out: {key: string; label: string; allowMiter: boolean; locked: boolean}[] = [];
    for(const c of spec.cornerInfo) {
        for(const axis of ["h", "v"] as CornerAxis[]) {
            if(c[axis].id !== stickId) continue;
            out.push({
                key: `${c.key}#${axis}`,
                label: c.label,
                allowMiter: c.allowMiter,
                locked: c.locked || !c.h.editable || !c.v.editable,
            });
        }
    }
    return out;
}

// the effective joint at a corner from the selected stick's perspective: its model
// override if set, else the corner default.
export function getCornerJoint(spec: MdFrameSpec, key: string): MdFrameJoint {
    const {corner, axis} = parseCornerKey(key);
    const c = spec.cornerInfo.find(i => i.key === corner);
    if(!c) return spec.frameBlockJointDefault.value;

    const self = c[axis];
    if(self.editable) {
        const override = endJoint(findStickById(spec, pieceStickId(self.id)), self.end);
        if(override) return override;
    }
    return c.defaults[axis];
}

// set a corner's joint from the selected stick's perspective. The selected stick
// gets `type`; the other gets the complement (extend<->cap, miter<->miter) so the
// pair stays consistent. Rejects a locked corner and a miter on a threshold corner.
export function setCornerJoint(spec: MdFrameSpec, key: string, type: MdFrameJoint): boolean {
    const {corner, axis} = parseCornerKey(key);
    const c = spec.cornerInfo.find(i => i.key === corner);
    if(!c || c.locked || !c.h.editable || !c.v.editable) return false;
    if(type === "miter" && !c.allowMiter) return false;

    const self = c[axis];
    const other = axis === "h" ? c.v : c.h;
    const selfStick = findStickById(spec, pieceStickId(self.id));
    const otherStick = findStickById(spec, pieceStickId(other.id));
    if(!selfStick || !otherStick) return false;

    setEndJoint(selfStick, self.end, type);
    setEndJoint(otherStick, other.end, type === "miter" ? "miter" : type === "extend" ? "cap" : "extend");
    return true;
}

// ---- frame outline steps ----------------------------------------------------
// A step is a void cell (space outside the frame) at the end of a block, behind the
// stick that used to lie on the frame edge there: stepping a perimeter piece inward
// turns it into an interior stick with a void behind it. "Split by" gives part of a
// perimeter edge its own stick, so that part can step on its own.

export type OutlineSide = "top" | "bottom" | "left" | "right";

// the block kind whose own leading/trailing stick lies on `side`: a column's first /
// last stick is on its top / bottom edge, a row's on its left / right edge.
function edgeOwnerKind(side: OutlineSide): "row" | "column" {
    return side === "top" || side === "bottom" ? "column" : "row";
}

function sideIsLeading(side: OutlineSide): boolean {
    return side === "top" || side === "left";
}

// a block's own stick on `side`: a stick child before its first cell or after its
// last, on the edge across its flow.
function ownEdgeStick(block: MdFrameBlock, side: OutlineSide): MdFrameStick | undefined {
    if(block.kind !== edgeOwnerKind(side)) return undefined;
    if(!block.children.some(isCell)) return undefined;
    const c = sideIsLeading(side) ? block.children[0] : block.children[block.children.length - 1];
    return c?.kind === "stick" ? c.stick : undefined;
}

// the indexes of a block's cells that touch `side`: every cell when the side runs
// along the block's flow, else the first or last cell.
function cellIndexesOnSide(block: MdFrameBlock, side: OutlineSide): number[] {
    const idx = block.children.map((c, i) => isCell(c) ? i : -1).filter(i => i >= 0);
    const along = (block.kind === "row") === (side === "top" || side === "bottom");
    if(along || idx.length === 0) return idx;
    return [sideIsLeading(side) ? idx[0] : idx[idx.length - 1]];
}

// true when a void lies on `side` of this cell's subtree.
function edgeHasVoid(c: MdFrameBlockChild | undefined, side: OutlineSide): boolean {
    if(!c) return false;
    if(c.kind === "item") return c.item.kind === "void";
    if(c.kind !== "block") return false;
    return cellIndexesOnSide(c.block, side).some(i => edgeHasVoid(c.block.children[i], side));
}

// the stick that covers `side` of the cell at the end of `path` on the frame
// outline: the deepest block on the way down with its own stick on that side, else
// the perimeter's piece. Null when the cell doesn't reach that side of the frame, or
// no stick covers it there (the door's open sill).
function outlineCover(spec: MdFrameSpec, path: PathStep[], side: OutlineSide): MdFrameStick | null {
    if(path.length === 0) return null;

    const last = path[path.length - 1];
    const cell = last.block.children[last.index];
    const own = cell.kind === "block" ? ownEdgeStick(cell.block, side) : undefined;
    return own ?? coverAround(spec, path, side);
}

// the stick that covers `side` around the cell at the end of `path` — what would cover
// it without its own edge stick: the deepest block above it with its own stick on that
// side, else the perimeter's piece. Null when the cell doesn't reach that side.
function coverAround(spec: MdFrameSpec, path: PathStep[], side: OutlineSide): MdFrameStick | null {
    if(path.length === 0) return null;

    let cover: MdFrameStick | null = null;
    for(const step of path) {
        cover = ownEdgeStick(step.block, side) ?? cover;
        if(!cellIndexesOnSide(step.block, side).includes(step.index)) return null;
    }
    if(cover) return cover;

    const last = path[path.length - 1];
    return perimeterCover(spec, last.block.children[last.index], side);
}

// the perimeter piece that covers `side` of a cell on the frame edge. A split bottom
// covers the cells left of the door with its left run and those right of it with its
// right run, by their order along the bottom edge (the door may sit deep in the tree);
// the door has an open sill.
function perimeterCover(spec: MdFrameSpec, cell: MdFrameBlockChild, side: OutlineSide): MdFrameStick | null {
    // no perimeter layer any more (see findPerimeterStick).
    return null;
}

// the id of the doors item, or null.
function findDoorLeafId(block: MdFrameBlock): string | null {
    for(const c of block.children) {
        if(c.kind === "item" && c.item.kind === "doors") return c.item.id;
        if(c.kind === "block") {
            const found = findDoorLeafId(c.block);
            if(found) return found;
        }
    }
    return null;
}

// the block that owns `stickId` as its own edge stick, and the side it lies on.
function findOwnEdgeStick(block: MdFrameBlock, stickId: string): {block: MdFrameBlock; side: OutlineSide} | null {
    for(const side of ["top", "bottom", "left", "right"] as OutlineSide[]) {
        if(ownEdgeStick(block, side)?.id === stickId) return {block, side};
    }

    for(const c of block.children) {
        if(c.kind !== "block") continue;
        const found = findOwnEdgeStick(c.block, stickId);
        if(found) return found;
    }
    return null;
}

// the side of the frame outline a perimeter piece lies on, or null when the stick
// isn't one (the threshold is never drawn, so it isn't either).
function outlineSide(spec: MdFrameSpec, stickId: string): OutlineSide | null {
    const loc = findPerimeterStick(spec.frameBlocks, stickId);
    if(loc) return loc.role === "threshold" ? null : loc.side;

    const own = findOwnEdgeStick(spec.frameBlocks, stickId);
    if(!own) return null;
    return own.side;
}

// a fresh perimeter stick like `src`: same thickness and face profiles.
function newStickLike(spec: MdFrameSpec, src: MdFrameStick): MdFrameStick {
    const id = nextId(spec, "stick");
    return {
        id,
        thickness: new FrmVar(id + ":thick", stickThicknessVar(src, spec).get()),
        profileLeft: src.profileLeft,
        profileRight: src.profileRight,
    };
}

// a cell's size along its parent's flow, from the last layout.
function laidOutSize(spec: MdFrameSpec, c: MdFrameBlockChild, kind: "row" | "column"): number {
    const id = c.kind === "item" ? c.item.id : c.kind === "block" ? c.block.id : c.stick.id;
    const size = spec.sizeInfo.get(id);
    if(c.kind === "stick") return stickThicknessVar(c.stick, spec).get();
    if(!size) return 0;
    return kind === "row" ? size.width : size.height;
}

// give the cells block.children[from..to] their own stick on `side`: a single block
// that can own a stick there takes it directly; otherwise the run is wrapped in one
// that can (inside a block of the parent's kind when it is more than one cell).
// Returns the block that owns the stick.
function giveOwnEdgeStick(spec: MdFrameSpec, block: MdFrameBlock, from: number, to: number, side: OutlineSide, stick: MdFrameStick): MdFrameBlock {
    const ownerKind = edgeOwnerKind(side);
    const run = block.children.slice(from, to + 1);
    const stickChild: MdFrameBlockChild = {kind: "stick", stick};

    // the run is the block's own edge cell and the block can own the stick: it takes
    // it directly.
    const edgeIdx = cellIndexesOnSide(block, side);
    if(block.kind === ownerKind && !ownEdgeStick(block, side) && from === to && edgeIdx.length === 1 && edgeIdx[0] === from) {
        if(sideIsLeading(side)) block.children.unshift(stickChild);
        else block.children.push(stickChild);
        return block;
    }

    const single = run.length === 1 ? run[0] : null;
    if(single?.kind === "block" && single.block.kind === ownerKind && !ownEdgeStick(single.block, side)) {
        if(sideIsLeading(side)) single.block.children.unshift(stickChild);
        else single.block.children.push(stickChild);
        return single.block;
    }

    // the size along `block`'s flow belongs to the slot, which the new owner takes
    // over: a single cell hands its size up (as splitItem does), and the cell starts
    // again from a soft size along the owner's flow. Inside the owner that size runs
    // the other way, so a pinned width would otherwise pin the cell's height.
    const flow = run.reduce((acc, c) => acc + laidOutSize(spec, c, block.kind), 0);
    const cross = spec.sizeInfo.get(block.id)?.[ownerKind === "column" ? "height" : "width"] ?? 0;
    const ownerId = nextId(spec, ownerKind);
    let ownerSize = new FrmVar(nextId(spec, "block-size"), flow);

    if(single?.kind === "item" && single.item.kind !== "doors" && single.item.size) {
        ownerSize = single.item.size;
        single.item.size = undefined;
    } else if(single?.kind === "block") {
        ownerSize = single.block.size;
        single.block.size = new FrmVar(single.block.id + ":size", cross);
    }
    ownerSize.rename(ownerId + ":size");

    const inner: MdFrameBlockChild = single ?? {
        kind: "block",
        block: {
            id: nextId(spec, block.kind),
            kind: block.kind,
            size: new FrmVar(nextId(spec, "block-size"), cross),
            children: run,
        },
    };

    const owner: MdFrameBlock = {
        id: ownerId,
        kind: ownerKind,
        size: ownerSize,
        children: sideIsLeading(side) ? [stickChild, inner] : [inner, stickChild],
    };
    block.children.splice(from, to - from + 1, {kind: "block", block: owner});
    return owner;
}

// the run of cells in `block` from `from` on whose `side` the stick covers, going
// forward (dir 1) or back (dir -1) — stopping at a cell another stick covers, a void,
// or the door. Returns the far cell's index.
function coveredRunEnd(spec: MdFrameSpec, toBlock: PathStep[], block: MdFrameBlock, from: number, side: OutlineSide, stick: MdFrameStick, dir: 1 | -1): number {
    let last = from;
    for(let k = from + dir; k >= 0 && k < block.children.length; k += dir) {
        const c = block.children[k];
        if(c.kind === "stick") continue;
        if(!coveredBy(spec, [...toBlock, {block, index: k}], side, stick)) break;
        last = k;
    }
    return last;
}

// true when `stick` covers `side` of the cell at the end of `path`, the whole way
// along: the cell has no void and (on the bottom) no door on that side.
function coveredBy(spec: MdFrameSpec, path: PathStep[], side: OutlineSide, stick: MdFrameStick): boolean {
    const last = path[path.length - 1];
    const cell = last.block.children[last.index];
    if(!isCell(cell) || edgeHasVoid(cell, side)) return false;
    if(side === "bottom" && cellContainsDoor(cell)) return false;
    return outlineCover(spec, path, side) === stick;
}

// true when `stick` covers `side` of the leaf cell at the first (or last) end of the
// cell at the end of `path`, along that side.
function coveredAtEnd(spec: MdFrameSpec, path: PathStep[], side: OutlineSide, stick: MdFrameStick, first: boolean): boolean {
    const last = path[path.length - 1];
    const cell = last.block.children[last.index];
    if(cell.kind !== "block") return coveredBy(spec, path, side, stick);

    const idx = cellIndexesOnSide(cell.block, side);
    if(idx.length === 0) return false;
    return coveredAtEnd(spec, [...path, {block: cell.block, index: first ? idx[0] : idx[idx.length - 1]}], side, stick, first);
}

// how "Split by" would split the perimeter piece `pieceId` at the stick `byId`: the
// cells after `byId` (in its block) that the piece covers get their own stick. When the
// piece covers only part of the cell after `byId` (e.g. it holds the door), the cells
// before `byId` get it instead. Null when `byId` isn't a stick that meets the piece
// between two cells it covers.
type SplitByPlan = {block: MdFrameBlock; from: number; to: number; side: OutlineSide; piece: MdFrameStick; dir: 1 | -1};

function splitByPlan(spec: MdFrameSpec, pieceId: string, byId: string): SplitByPlan | null {
    // a sloped stick splits only once it is straight again.
    if(findStickById(spec, pieceId)?.slope) return null;
    if(pieceId === byId) return null;

    const side = outlineSide(spec, pieceId);
    const piece = findStickById(spec, pieceId);
    if(!side || !piece) return null;

    const at = findStickPath(spec.frameBlocks, byId);
    if(!at) return null;

    const block = at.block;
    if(block.kind !== (side === "top" || side === "bottom" ? "row" : "column")) return null;

    const toBlock = findBlockPath(spec.frameBlocks, block.id);
    if(!toBlock) return null;

    const before = [...toBlock, {block, index: at.index - 1}];
    const after = [...toBlock, {block, index: at.index + 1}];
    if(coveredBy(spec, after, side, piece) && coveredAtEnd(spec, before, side, piece, false)) {
        const to = coveredRunEnd(spec, toBlock, block, at.index + 1, side, piece, 1);
        return {block, from: at.index + 1, to, side, piece, dir: 1};
    }

    if(coveredBy(spec, before, side, piece) && coveredAtEnd(spec, after, side, piece, true)) {
        const from = coveredRunEnd(spec, toBlock, block, at.index - 1, side, piece, -1);
        return {block, from, to: at.index - 1, side, piece, dir: -1};
    }

    return null;
}

// the leaf cells along the edge past a Split By run (in the plan's direction) that the
// piece still covers, up to the first one it doesn't.
function splitByRest(spec: MdFrameSpec, plan: SplitByPlan): string[] {
    const root = spec.frameBlocks;
    const order = edgeLeafIds({kind: "block", block: root}, plan.side);
    const run = plan.block.children.slice(plan.from, plan.to + 1).flatMap(c => edgeLeafIds(c, plan.side));
    const pos = run.map(id => order.indexOf(id));
    if(pos.length === 0 || pos.includes(-1)) return [];

    const out: string[] = [];
    const start = plan.dir === 1 ? Math.max(...pos) + 1 : Math.min(...pos) - 1;
    for(let k = start; k >= 0 && k < order.length; k += plan.dir) {
        const path = findItemPath(root, order[k]);
        if(!path || !coveredBy(spec, path, plan.side, plan.piece)) break;
        out.push(order[k]);
    }
    return plan.dir === 1 ? out : out.reverse();
}

// how "Split by" would unlink the joined sticks of `pieceId` (see joinSticks) at the
// stick `byId`: the linked sticks before `byId` along the edge and those after it, each
// in edge order. Null when `byId` doesn't sit where two of the linked sticks meet.
function unlinkPlan(spec: MdFrameSpec, pieceId: string, byId: string): {before: string[]; after: string[]} | null {
    if(pieceId === byId) return null;

    const side = joinSide(spec, pieceId);
    if(!side) return null;

    const sticks = sticksById(spec.frameBlocks);
    const root = joinedRootId(sticks, pieceId);
    const group = Array.from(sticks.keys()).filter(id => joinedRootId(sticks, id) === root);
    if(group.length < 2) return null;

    const at = findStickPath(spec.frameBlocks, byId);
    if(!at || at.block.kind !== (side === "top" || side === "bottom" ? "row" : "column")) return null;

    const beforeCell = at.block.children[at.index - 1];
    const afterCell = at.block.children[at.index + 1];
    if(!isCell(beforeCell) || !isCell(afterCell)) return null;

    const lastBefore = edgeLeafIds(beforeCell, side).pop();
    const firstAfter = edgeLeafIds(afterCell, side)[0];
    const order = edgeLeafIds({kind: "block", block: spec.frameBlocks}, side);
    const cut = firstAfter ? order.indexOf(firstAfter) : -1;
    if(!lastBefore || cut < 0) return null;

    const parts: {id: string; leaves: string[]; min: number; max: number}[] = [];
    for(const id of group) {
        const owner = findEdgeOwner(spec, id);
        if(!owner) return null;

        const leaves = edgeLeafIds({kind: "block", block: owner.block}, side);
        const pos = leaves.map(l => order.indexOf(l));
        if(pos.length === 0 || pos.includes(-1)) return null;
        parts.push({id, leaves, min: Math.min(...pos), max: Math.max(...pos)});
    }
    if(parts.some(p => p.min < cut && p.max >= cut)) return null;

    parts.sort((a, b) => a.min - b.min);
    const before = parts.filter(p => p.max < cut);
    const after = parts.filter(p => p.min >= cut);
    if(!before.some(p => p.leaves.includes(lastBefore)) || !after.some(p => p.leaves.includes(firstAfter))) return null;

    return {before: before.map(p => p.id), after: after.map(p => p.id)};
}

// make the two groups of an unlink plan two separate joined sticks. The group without
// the old root gets a step size of its own. Returns the id the group holding `pieceId`
// draws as.
function unlinkSticks(spec: MdFrameSpec, pieceId: string, plan: {before: string[]; after: string[]}): string {
    const sticks = sticksById(spec.frameBlocks);
    const voids = stepVoids(spec.frameBlocks);
    const oldRoot = joinedRootId(sticks, pieceId);

    let pieceHead = pieceId;
    for(const ids of [plan.before, plan.after]) {
        const head = ids.includes(oldRoot) ? oldRoot : ids[0];
        ids.forEach(id => sticks.get(id)!.joinedTo = id === head ? undefined : head);
        if(ids.includes(pieceId)) pieceHead = head;

        if(ids.includes(oldRoot)) continue;
        const steps = ids.map(id => voids.get(id)).filter((v): v is MdFrameItem & {kind: "void"} => v?.kind === "void" && !!v.size);
        if(steps.length === 0) continue;

        const size = new FrmVar(nextId(spec, "void-size"), 0);
        size.userSet(steps[0].size!.get(), {debug: "unlink"});
        steps.forEach(v => v.size = size);
    }
    return pieceHead;
}

// true when "Split by" can split the perimeter piece `pieceId` at the stick `byId`, or
// unlink its joined sticks there.
export function canSplitPerimeterBy(spec: MdFrameSpec, pieceId: string, byId: string): boolean {
    return splitByPlan(spec, pieceId, byId) !== null || unlinkPlan(spec, pieceId, byId) !== null;
}

// the ids of the sticks that could split the perimeter piece `pieceId`.
export function splitByCandidates(spec: MdFrameSpec, pieceId: string): string[] {
    if(!outlineSide(spec, pieceId) && !joinSide(spec, pieceId)) return [];
    return interiorStickIds(spec.frameBlocks).filter(id => canSplitPerimeterBy(spec, pieceId, id));
}

// true when some stick could split the perimeter piece `pieceId` (enables the
// Split By button).
export function canStartSplitBy(spec: MdFrameSpec, pieceId: string): boolean {
    return splitByCandidates(spec, pieceId).length > 0;
}

// split the perimeter piece `pieceId` where the stick `byId` meets it: the part past
// `byId` gets a new stick of its own, and the piece keeps the rest. Joined sticks that
// meet at `byId` unlink there instead. Returns the id of the stick to select, or null
// when `byId` can't split it (see splitByPlan, unlinkPlan).
export function splitPerimeterBy(spec: MdFrameSpec, pieceId: string, byId: string): string | null {
    const plan = splitByPlan(spec, pieceId, byId);
    if(plan) {
        const root = spec.frameBlocks;
        const rest = splitByRest(spec, plan);
        const own = findOwnEdgeStick(root, plan.piece.id);

        // the cells the piece keeps: those it covers that the split doesn't take.
        const taken = new Set([...plan.block.children.slice(plan.from, plan.to + 1).flatMap(c => edgeLeafIds(c, plan.side)), ...rest]);
        const keep = !own ? [] : edgeLeafIds({kind: "block", block: own.block}, plan.side).filter(id => {
            const path = findItemPath(root, id);
            return !taken.has(id) && !!path && coveredBy(spec, path, plan.side, plan.piece);
        });

        const stick = newStickLike(spec, plan.piece);
        giveOwnEdgeStick(spec, plan.block, plan.from, plan.to, plan.side, stick);

        // the cells past the run in other blocks get sticks joined to the new one, so
        // it runs on to where the piece ended.
        for(const run of groupLeafRuns(root, rest, plan.side) ?? []) {
            const s = newStickLike(spec, plan.piece);
            s.joinedTo = stick.id;
            giveOwnEdgeStick(spec, run.block, run.from, run.to, plan.side, s);
        }

        // the piece comes off its block and down onto the cells it keeps, so it lies in
        // line with the new stick rather than along the whole block behind it.
        if(own && keep.length > 0) {
            moveOwnStickDown(spec, own.block, plan.side, keep, true);
            collapseWrappers(spec);
        }
        return stick.id;
    }

    const unlink = unlinkPlan(spec, pieceId, byId);
    if(!unlink) return null;
    return unlinkSticks(spec, pieceId, unlink);
}

// the ids of every stick between two cells.
function interiorStickIds(block: MdFrameBlock): string[] {
    const out: string[] = [];
    block.children.forEach((c, i) => {
        if(c.kind === "stick" && isCell(block.children[i - 1]) && isCell(block.children[i + 1])) out.push(c.stick.id);
        if(c.kind === "block") out.push(...interiorStickIds(c.block));
    });
    return out;
}

// step the perimeter piece `stickId` on `side` inward: put a void of `size` behind it.
// `leafIds` are the cells the drawn piece covers (one stick may be drawn as several
// pieces). A block's own edge stick steps where it is; a perimeter stick first gives
// its cells a new stick of their own (the perimeter stick stays on the rest of the
// edge). A block's own stick that covers only part of the block's edge (a block
// below has its own stick or step there) first moves down onto the cells it covers.
// Returns the id of the stick that stepped, or null when the piece can't step (the
// door would give up the space).
export function stepOutlinePiece(spec: MdFrameSpec, stickId: string, side: OutlineSide, leafIds: string[], size: number): string | null {
    return makeStep(spec, stickId, side, leafIds, size, true);
}

// see stepOutlinePiece. With `keepSize`, the cell beside the stick gives up the space
// the step takes, so the stepped block keeps its size; else the block grows by it.
function makeStep(spec: MdFrameSpec, stickId: string, side: OutlineSide, leafIds: string[], size: number, keepSize: boolean): string | null {
    const root = spec.frameBlocks;
    const own = findOwnEdgeStick(root, stickId);

    let owner: MdFrameBlock | null = null;
    let stick: MdFrameStick;

    if(own && own.side === side) {
        owner = own.block;
        stick = ownEdgeStick(owner, side)!;

        // a block further down has its own stick on this side, so this stick covers
        // only part of the block's edge: move the stick down onto the cells it covers.
        // Else the cell beside the stick gives up the space for the void (keepSize),
        // which the door can't. A void may never lie under the door either; beside or
        // above it, one may, when another part's growth makes it (see canStepFresh).
        const doorOk = !keepSize && side !== "bottom";
        if(!hasNestedEdgeStick(owner, side)) {
            if(!doorOk && cellContainsDoor(sideIsLeading(side) ? owner.children[1] : owner.children[owner.children.length - 2])) return null;
        } else {
            const made = moveOwnStickDown(spec, owner, side, leafIds, doorOk);
            if(!made) return null;
            stick = made[0].stick;
            owner = made[0].owner;
        }
    } else {
        const piece = findPerimeterStick(root, stickId);
        if(!piece || piece.side !== side) return null;
        stick = piece.stick;

        const runs = leafRuns(root, leafIds, side);
        if(!runs) return null;
        const made = giveRunsSticks(spec, runs, side, newStickLike(spec, piece.stick));
        stick = made[0].stick;
        owner = made[0].owner;
    }
    if(!owner) return null;

    const voidSize = new FrmVar(nextId(spec, "void-size"), 0);
    voidSize.userSet(size, {debug: "step"});

    // the sticks joined to this one step with it, at the same size.
    const sticks = sticksById(root);
    const owners = [owner, ...Array.from(sticks.values())
        .filter(s => s !== stick && joinedRootId(sticks, s.id) === stick.id)
        .map(s => findOwnEdgeStick(root, s.id))
        .filter((o): o is {block: MdFrameBlock; side: OutlineSide} => !!o && o.side === side)
        .map(o => o.block)];

    for(const o of owners) {
        // the stick moves inside the block, with the void in front of it. The stick's
        // thickness is in the block's size either way. With keepSize, the cell beside
        // the stick gives the void's space up, so the block keeps its size and the
        // parts beside it don't move.
        const beside = sideIsLeading(side) ? o.children[1] : o.children[o.children.length - 2];
        if(keepSize) shrinkCell(beside, size);

        const cell: MdFrameBlockChild = {kind: "item", item: {id: nextId(spec, "void"), kind: "void", size: voidSize}};
        if(sideIsLeading(side)) o.children.unshift(cell);
        else o.children.push(cell);
    }

    // the sticks of the blocks around the new voids come off them right away, so the
    // tree is right during the drag that goes on from here.
    normalizeEdgeSticks(spec);
    return stick.id;
}

type MadeStick = {stick: MdFrameStick; owner: MdFrameBlock};

// give each run of cells (see leafRuns) its own stick on `side`: the first run takes
// `first`, each further run a stick like it joined to the first, so they draw and
// move as one. Returns the sticks with the blocks that own them.
function giveRunsSticks(spec: MdFrameSpec, runs: LeafRun[], side: OutlineSide, first: MdFrameStick): MadeStick[] {
    const made = runs.map((run, i) => {
        const s = i === 0 ? first : newStickLike(spec, first);
        return {stick: s, owner: giveOwnEdgeStick(spec, run.block, run.from, run.to, side, s)};
    });
    made.slice(1).forEach(m => m.stick.joinedTo = first.id);
    return made;
}

// take a block's own stick on `side` off the block and give it to the cells `leafIds`
// instead, in as few runs as the tree allows (see giveRunsSticks). Returns the sticks
// made, or null when the cells fit no runs (or hold the door, unless `allowDoor`): the
// block then keeps its stick.
function moveOwnStickDown(spec: MdFrameSpec, owner: MdFrameBlock, side: OutlineSide, leafIds: string[], allowDoor: boolean): MadeStick[] | null {
    const stick = ownEdgeStick(owner, side);
    if(!stick) return null;

    const children = owner.children;
    const at = sideIsLeading(side) ? 0 : children.length - 1;
    children.splice(at, 1);

    const root = spec.frameBlocks;
    const runs = allowDoor ? groupLeafRuns(root, leafIds, side) : leafRuns(root, leafIds, side);
    if(!runs) {
        children.splice(at, 0, {kind: "stick", stick});
        return null;
    }
    return giveRunsSticks(spec, runs, side, stick);
}

// take `amount` off a cell's size along its parent's flow, down to MIN_BLOCK. A cell
// with no size of its own (or the door) is left to the solver.
function shrinkCell(cell: MdFrameBlockChild | undefined, amount: number) {
    const size = cell?.kind === "block" ? cell.block.size
        : cell?.kind === "item" && cell.item.kind !== "doors" ? cell.item.size
        : undefined;
    if(!size) return;
    size.set(Math.max(MIN_BLOCK, size.get() - amount), {debug: "step"});
}

// true when a block below `block` has its own stick on `side`, on the edge or already
// stepped (it covers part of the edge `block`'s stick covers, so the step would leave
// it behind).
function hasNestedEdgeStick(block: MdFrameBlock, side: OutlineSide): boolean {
    return cellIndexesOnSide(block, side).some(i => {
        const c = block.children[i];
        if(c.kind !== "block") return false;
        return !!edgeOrStepStick(c.block, side) || hasNestedEdgeStick(c.block, side);
    });
}

type LeafRun = {block: MdFrameBlock; from: number; to: number};

// the run of sibling cells that holds exactly the cells `leafIds` along `side`: the
// deepest block holding them all, and the range of its children they fill. A single
// cell in a block that can own a stick on `side` is taken as that whole block.
// Null when the cells don't fill a contiguous run there.
function leafRun(root: MdFrameBlock, leafIds: string[], side: OutlineSide): LeafRun | null {
    const paths = leafIds.map(id => findItemPath(root, id));
    if(paths.length === 0 || paths.some(p => !p)) return null;

    // the depth down to which every path takes the same steps.
    let depth = 0;
    for(;;) {
        const step = paths[0]![depth];
        if(depth >= paths[0]!.length - 1 || paths.some(p => p![depth]?.block !== step.block || p![depth].index !== step.index)) break;
        depth++;
    }

    let block = paths[0]![depth].block;
    const indexes = paths.map(p => p![depth].index);
    let from = Math.min(...indexes);
    let to = Math.max(...indexes);

    // a run that fills the block's whole edge on `side` is the block itself: take it
    // as one cell of its parent, up to a parent with its own stick on that side.
    while(depth > 0) {
        const parent = paths[0]![depth - 1];
        if(ownEdgeStick(parent.block, side)) break;
        if(!cellIndexesOnSide(block, side).every(i => i >= from && i <= to)) break;
        if(!cellIndexesOnSide(parent.block, side).includes(parent.index)) break;
        depth--;
        block = parent.block;
        from = to = parent.index;
    }

    // every cell on `side` inside the run must be one of the piece's cells.
    const leaves = new Set(leafIds);
    const runCovered = block.children.slice(from, to + 1).every(c => !isCell(c) || edgeLeafIds(c, side).every(id => leaves.has(id)));
    return runCovered ? {block, from, to} : null;
}

// split the cells `leafIds` (in order along `side`) into runs that each fill a
// contiguous range of one block's children (see leafRun), as few as possible. Null
// when a cell can't be put in a run, or a run holds the door.
function leafRuns(root: MdFrameBlock, leafIds: string[], side: OutlineSide): LeafRun[] | null {
    const runs = groupLeafRuns(root, leafIds, side);
    if(!runs || runs.some(r => r.block.children.slice(r.from, r.to + 1).some(cellContainsDoor))) return null;
    return runs;
}

// the cells `leafIds` (in edge order) in as few runs as the tree allows: each run is
// the sibling cells of one block (see leafRun). Null when a cell fits no run.
function groupLeafRuns(root: MdFrameBlock, leafIds: string[], side: OutlineSide): LeafRun[] | null {
    const groups: string[][] = [];
    for(const id of leafIds) {
        const last = groups[groups.length - 1];
        if(last && leafRun(root, [...last, id], side)) last.push(id);
        else groups.push([id]);
    }

    const runs = groups.map(g => leafRun(root, g, side));
    if(runs.some(r => !r)) return null;
    return runs as LeafRun[];
}

// the ids of the leaf cells along `side` of a cell's subtree.
function edgeLeafIds(c: MdFrameBlockChild, side: OutlineSide): string[] {
    if(c.kind === "item") return [c.item.id];
    if(c.kind !== "block") return [];
    return cellIndexesOnSide(c.block, side).flatMap(i => edgeLeafIds(c.block.children[i], side));
}

// ---- join (the opposite of Split By) ----------------------------------------
// Join folds stick B into stick A on the frame outline, so A covers both parts:
//   - B is a block's own edge stick and A covers the edge around that block: B and
//     the sticks joined with it (and their steps, if any) go, and that part goes back
//     to A.
//   - the reverse: A was split off B. A's own stick goes and the edge goes back to B,
//     which then covers both (B's id survives).
//   - A and B are the own sticks of two sibling blocks with one stick between them:
//     the two blocks become one with A's stick (and A's step, if any). The heights
//     don't need to match: the joined part takes A's.
//   - A and B lie next to each other along the edge but in blocks that can't become
//     one (e.g. a transom runs over one and not the other): B is linked to A
//     (joinedTo). They draw as one stick, and their steps share A's size.
// A stick here is a block's own edge stick, or a step stick (one with a void behind it).

// a block's stick on `side` of the frame outline: its own edge stick, or its step
// stick (the stick in front of a void at that end).
function edgeOrStepStick(block: MdFrameBlock, side: OutlineSide): MdFrameStick | undefined {
    const own = ownEdgeStick(block, side);
    if(own) return own;
    if(block.kind !== edgeOwnerKind(side)) return undefined;

    const c = block.children;
    const [end, next] = sideIsLeading(side) ? [c[0], c[1]] : [c[c.length - 1], c[c.length - 2]];
    if(end?.kind !== "item" || end.item.kind !== "void" || next?.kind !== "stick") return undefined;
    return next.stick;
}

// the stick a joined stick finally joins into (itself when it isn't joined).
function joinedRoot(spec: MdFrameSpec, id: string): string {
    return joinedRootId(sticksById(spec.frameBlocks), id);
}

// the side of the frame outline a stick lies on for Join: a perimeter piece, a
// block's own edge stick or a step stick. Null otherwise.
function joinSide(spec: MdFrameSpec, stickId: string): OutlineSide | null {
    const loc = findPerimeterStick(spec.frameBlocks, stickId);
    if(loc) return loc.role === "threshold" ? null : loc.side;
    return findEdgeOwner(spec, stickId)?.side ?? null;
}

// a block that owns a stick on the frame outline, with where it sits in the tree.
type EdgeOwner = {block: MdFrameBlock; side: OutlineSide; path: PathStep[]};

function findEdgeOwner(spec: MdFrameSpec, stickId: string): EdgeOwner | null {
    const find = (block: MdFrameBlock): {block: MdFrameBlock; side: OutlineSide} | null => {
        for(const side of ["top", "bottom", "left", "right"] as OutlineSide[]) {
            if(edgeOrStepStick(block, side)?.id === stickId) return {block, side};
        }
        for(const c of block.children) {
            if(c.kind !== "block") continue;
            const found = find(c.block);
            if(found) return found;
        }
        return null;
    };

    const own = find(spec.frameBlocks);
    if(!own) return null;
    const path = findBlockPath(spec.frameBlocks, own.block.id);
    if(!path || path.length === 0) return null;
    return {block: own.block, side: own.side, path};
}

// true when the two owners' parts of the edge are next to each other along it.
function edgeNeighbours(spec: MdFrameSpec, a: EdgeOwner, b: EdgeOwner, side: OutlineSide): boolean {
    const order = edgeLeafIds({kind: "block", block: spec.frameBlocks}, side);
    const leavesA = edgeLeafIds({kind: "block", block: a.block}, side).map(id => order.indexOf(id));
    const leavesB = edgeLeafIds({kind: "block", block: b.block}, side).map(id => order.indexOf(id));
    if(leavesA.includes(-1) || leavesB.includes(-1)) return false;

    return Math.max(...leavesA) + 1 === Math.min(...leavesB) || Math.max(...leavesB) + 1 === Math.min(...leavesA);
}

type JoinPlan =
    | {kind: "drop"; ids: string[]; keep: string}
    | {kind: "siblings"; a: EdgeOwner; b: EdgeOwner}
    | {kind: "link"; a: EdgeOwner; b: EdgeOwner};

function joinPlan(spec: MdFrameSpec, aId: string, bId: string): JoinPlan | null {
    if(aId === bId) return null;
    // a sloped stick joins only once it is straight again.
    if(findStickById(spec, aId)?.slope || findStickById(spec, bId)?.slope) return null;

    const side = joinSide(spec, aId);
    if(!side || joinSide(spec, bId) !== side) return null;
    if(joinedRoot(spec, aId) === joinedRoot(spec, bId)) return null;

    const a = findEdgeOwner(spec, aId);
    const b = findEdgeOwner(spec, bId);

    const bGroup = dropGroup(spec, bId, aId, side);
    if(bGroup) return {kind: "drop", ids: bGroup, keep: aId};
    const aGroup = dropGroup(spec, aId, bId, side);
    if(aGroup) return {kind: "drop", ids: aGroup, keep: bId};

    if(a && b && a.side === side && b.side === side) {
        const pa = a.path[a.path.length - 1];
        const pb = b.path[b.path.length - 1];
        if(pa.block === pb.block && Math.abs(pa.index - pb.index) === 2 && pa.block.children[(pa.index + pb.index) / 2]?.kind === "stick") {
            return {kind: "siblings", a, b};
        }

        if(!!ownerStep(a) === !!ownerStep(b) && edgeNeighbours(spec, a, b, side)) {
            return {kind: "link", a, b};
        }
    }
    return null;
}

// the sticks to drop when `id` folds back into `keepId`: `id` and every stick joined
// with it, so no part of them is left on the edge. Null when `keepId` doesn't cover
// the edge around each of them.
function dropGroup(spec: MdFrameSpec, id: string, keepId: string, side: OutlineSide): string[] | null {
    const sticks = sticksById(spec.frameBlocks);
    const root = joinedRootId(sticks, id);
    const ids = Array.from(sticks.keys()).filter(s => joinedRootId(sticks, s) === root);

    for(const s of ids) {
        const owner = findEdgeOwner(spec, s);
        if(!owner || owner.side !== side || coverAround(spec, owner.path, side)?.id !== keepId) return null;
    }
    return ids;
}

// true when Join can fold `bId` into `aId`.
export function canJoinSticks(spec: MdFrameSpec, aId: string, bId: string): boolean {
    return joinPlan(spec, aId, bId) !== null;
}

// the ids of the sticks that can join `aId`.
export function joinCandidates(spec: MdFrameSpec, aId: string): string[] {
    if(!joinSide(spec, aId)) return [];
    return outlineStickIds(spec).filter(id => canJoinSticks(spec, aId, id));
}

// true when some stick can join `aId` (enables the Join button).
export function canStartJoin(spec: MdFrameSpec, aId: string): boolean {
    return joinCandidates(spec, aId).length > 0;
}

// fold `bId` into `aId` (see the section comment). Returns the id of the stick that
// covers both parts now, or null when they can't join.
export function joinSticks(spec: MdFrameSpec, aId: string, bId: string): string | null {
    const plan = joinPlan(spec, aId, bId);
    if(!plan) return null;

    if(plan.kind === "drop") {
        for(const id of plan.ids) {
            const owner = findEdgeOwner(spec, id);
            if(owner) dropOwnEdgeStick(owner);
        }
        return plan.keep;
    }

    if(plan.kind === "siblings") {
        const bStick = edgeOrStepStick(plan.b.block, plan.b.side)!;
        joinSiblingOwners(spec, plan.a, plan.b);

        // B's stick is gone, so the sticks joined to it join A.
        for(const s of sticksById(spec.frameBlocks).values()) {
            if(s.joinedTo === bStick.id) s.joinedTo = aId;
        }
        return aId;
    }

    // link: B draws as part of A, and B's step takes A's size.
    const bStick = edgeOrStepStick(plan.b.block, plan.b.side)!;
    bStick.joinedTo = aId;
    const aStep = ownerStep(plan.a);
    const bStep = ownerStep(plan.b);
    if(aStep?.kind === "item" && bStep?.kind === "item" && aStep.item.kind === "void" && bStep.item.kind === "void" && aStep.item.size) {
        bStep.item.size = aStep.item.size;
    }
    return aId;
}

// every stick that is a block's own edge stick or a step stick.
function outlineStickIds(spec: MdFrameSpec): string[] {
    const out: string[] = [];

    const walk = (block: MdFrameBlock) => {
        for(const side of ["top", "bottom", "left", "right"] as OutlineSide[]) {
            const own = edgeOrStepStick(block, side);
            if(own) out.push(own.id);
        }
        for(const c of block.children) if(c.kind === "block") walk(c.block);
    };
    walk(spec.frameBlocks);
    return out;
}

// every stick that is a block's own edge stick or a step stick, with the side of the
// frame outline it faces (for a step stick: the side its void is on).
export function outlineStickSides(spec: MdFrameSpec): Map<string, OutlineSide> {
    const out = new Map<string, OutlineSide>();

    const walk = (block: MdFrameBlock) => {
        for(const side of ["top", "bottom", "left", "right"] as OutlineSide[]) {
            const own = edgeOrStepStick(block, side);
            if(own) out.set(own.id, side);
        }
        for(const c of block.children) if(c.kind === "block") walk(c.block);
    };
    walk(spec.frameBlocks);
    return out;
}

// what part of the frame outline each outline stick covers, as text: its side, whether
// it is a step stick, and the first and last leaf cells beside it (its ends). Two values
// differ when a change moved an end of the stick (a step, a split, a stick moved off a
// void); a cell split between its ends does not.
export function outlineStickCoverage(spec: MdFrameSpec): Map<string, string> {
    const out = new Map<string, string>();

    const walk = (block: MdFrameBlock) => {
        for(const side of ["top", "bottom", "left", "right"] as OutlineSide[]) {
            const stick = edgeOrStepStick(block, side);
            if(!stick) continue;

            const at = block.children.findIndex(c => c.kind === "stick" && c.stick === stick);
            const beside = block.children[sideIsLeading(side) ? at + 1 : at - 1];
            const step = ownEdgeStick(block, side) !== stick;
            const leaves = beside ? edgeLeafIds(beside, side) : [];
            out.set(stick.id, `${side}|${step}|${leaves[0] ?? ""}|${leaves[leaves.length - 1] ?? ""}`);
        }
        for(const c of block.children) if(c.kind === "block") walk(c.block);
    };
    walk(spec.frameBlocks);

    for(const m of voidMullions(spec)) {
        const leaves = edgeLeafIds(m.beside, oppositeSide(m.side));
        out.set(m.stick.id, `${m.side}|mullion|${leaves.join(",")}`);
    }
    return out;
}

// every interior stick with a void beside it on one side only, with the side of the frame
// outline it faces (the side the void is on). Its pieces beside the void lie on the frame
// outline (e.g. the head over a step). Step sticks are left out (see outlineStickSides).
export function voidMullionSides(spec: MdFrameSpec): Map<string, OutlineSide> {
    return new Map(voidMullions(spec).map(m => [m.stick.id, m.side]));
}

// see voidMullionSides. `beside` is the cell on the void's side of the stick.
function voidMullions(spec: MdFrameSpec): {stick: MdFrameStick; side: OutlineSide; beside: MdFrameBlockChild}[] {
    const outline = outlineStickSides(spec);
    const out: {stick: MdFrameStick; side: OutlineSide; beside: MdFrameBlockChild}[] = [];

    const walk = (block: MdFrameBlock) => {
        const [leadSide, trailSide]: OutlineSide[] = block.kind === "row" ? ["left", "right"] : ["top", "bottom"];
        block.children.forEach((c, i) => {
            if(c.kind === "block") walk(c.block);
            if(c.kind !== "stick" || outline.has(c.stick.id)) return;

            const lead = block.children[i - 1];
            const trail = block.children[i + 1];
            if(!isCell(lead) || !isCell(trail)) return;

            const voidLead = edgeHasVoid(lead, trailSide);
            const voidTrail = edgeHasVoid(trail, leadSide);
            if(voidLead === voidTrail) return;
            out.push(voidLead ? {stick: c.stick, side: leadSide, beside: lead} : {stick: c.stick, side: trailSide, beside: trail});
        });
    };
    walk(spec.frameBlocks);
    return out;
}

function oppositeSide(side: OutlineSide): OutlineSide {
    switch(side) {
        case "top": return "bottom";
        case "bottom": return "top";
        case "left": return "right";
        case "right": return "left";
    }
}

// remove a block's own edge stick (and the void behind it, if it is a step), then let
// a block left holding one cell give way to that cell.
function dropOwnEdgeStick(owner: EdgeOwner) {
    const children = owner.block.children;
    if(sideIsLeading(owner.side)) {
        const n = children[0]?.kind === "item" && children[0].item.kind === "void" ? 2 : 1;
        children.splice(0, n);
    } else {
        const last = children.length - 1;
        const n = children[last]?.kind === "item" && children[last].item.kind === "void" ? 2 : 1;
        children.splice(children.length - n, n);
    }

    const at = owner.path[owner.path.length - 1];
    unwrapSingleCell(at.block, at.index);
}

// the cells of an owner block without its own edge stick and its step.
function ownerContent(owner: EdgeOwner): MdFrameBlockChild[] {
    const children = [...owner.block.children];
    const isVoid = (c?: MdFrameBlockChild) => c?.kind === "item" && c.item.kind === "void";
    if(sideIsLeading(owner.side)) {
        if(isVoid(children[0])) children.shift();
        children.shift();
    } else {
        if(isVoid(children[children.length - 1])) children.pop();
        children.pop();
    }
    return children;
}

// the step (void) behind an owner's own edge stick, if any.
function ownerStep(owner: EdgeOwner): MdFrameBlockChild | undefined {
    const children = owner.block.children;
    const c = sideIsLeading(owner.side) ? children[0] : children[children.length - 1];
    return c?.kind === "item" && c.item.kind === "void" ? c : undefined;
}

// make two sibling owner blocks one: their cells (with the stick between them) go
// into one block of the parent's kind, under A's stick and A's step.
function joinSiblingOwners(spec: MdFrameSpec, a: EdgeOwner, b: EdgeOwner) {
    const pa = a.path[a.path.length - 1];
    const pb = b.path[b.path.length - 1];
    const parent = pa.block;
    const first = Math.min(pa.index, pb.index);
    const [left, right] = pa.index < pb.index ? [a, b] : [b, a];
    const between = parent.children[first + 1];

    // one owner's cells as a single cell of the parent's kind: a lone cell keeps its
    // place (it takes the owner's size along the parent's flow); several stacked cells
    // stay together in a block of the owner's kind.
    const asCell = (o: EdgeOwner): MdFrameBlockChild[] => {
        const content = ownerContent(o);
        const cells = content.filter(isCell);
        if(cells.length === 1 && content.length === 1) {
            const c = cells[0];
            if(c.kind === "block" && c.block.kind === parent.kind) return c.block.children;
            if(c.kind === "item" && c.item.kind !== "doors") {
                c.item.size = o.block.size;
                c.item.size.rename(c.item.id + ":size");
            } else if(c.kind === "block") {
                c.block.size = o.block.size;
                c.block.size.rename(c.block.id + ":size");
            }
            return [c];
        }
        return [{kind: "block", block: {id: nextId(spec, o.block.kind), kind: o.block.kind, size: o.block.size, children: content}}];
    };

    const ownerKind = a.block.kind;
    const stick = edgeOrStepStick(a.block, a.side)!;
    const step = ownerStep(a);
    const cross = spec.sizeInfo.get(a.block.id)?.[ownerKind === "column" ? "height" : "width"] ?? 0;
    const flow = laidOutSize(spec, parent.children[pa.index], parent.kind) + laidOutSize(spec, between, parent.kind) + laidOutSize(spec, parent.children[pb.index], parent.kind);

    const inner: MdFrameBlockChild = {
        kind: "block",
        block: {
            id: nextId(spec, parent.kind),
            kind: parent.kind,
            size: new FrmVar(nextId(spec, "block-size"), cross),
            children: [...asCell(left), between, ...asCell(right)],
        },
    };

    const edge: MdFrameBlockChild[] = step ? [{kind: "stick", stick}, step] : [{kind: "stick", stick}];
    const owner: MdFrameBlock = {
        id: nextId(spec, ownerKind),
        kind: ownerKind,
        size: new FrmVar(nextId(spec, "block-size"), flow),
        children: sideIsLeading(a.side) ? [...edge.reverse(), inner] : [inner, ...edge],
    };

    parent.children.splice(first, 3, {kind: "block", block: owner});
}

// replace the block at parent.children[index] with its only cell, when it holds just
// one cell and no sticks. An item takes over the block's size (its slot along the
// parent's flow); a block of the parent's kind is flattened into the parent.
function unwrapSingleCell(parent: MdFrameBlock, index: number) {
    const c = parent.children[index];
    if(c?.kind !== "block") return;

    const inner = c.block;
    if(inner.children.some(cc => cc.kind === "stick")) return;
    const cells = inner.children.filter(isCell);
    if(cells.length !== 1) return;

    const cell = cells[0];
    if(cell.kind === "item") {
        if(cell.item.kind !== "doors") {
            cell.item.size = inner.size;
            cell.item.size.rename(cell.item.id + ":size");
        }
        parent.children[index] = cell;
        return;
    }

    if(cell.kind === "block" && cell.block.kind === parent.kind) {
        parent.children.splice(index, 1, ...cell.block.children);
        return;
    }

    if(cell.kind === "block") {
        cell.block.size = inner.size;
        cell.block.size.rename(cell.block.id + ":size");
        parent.children[index] = cell;
    }
}

// ---- resize a split part ----------------------------------------------------
// A cell alone in a block with its own edge stick (a split part of the frame edge)
// changes size through a step, so the parts beside it keep their size. The cell is a
// lite, or the block that wraps one with its stick on another side (see
// normalizeEdgeSticks):
//   - smaller: the step behind its stick takes the space (a new step must clear
//     VOID_ZERO, else the edit is refused).
//   - it already has a step: the step takes the change. A step at or under VOID_ZERO
//     goes back to the edge.
//   - larger than the row it sits in: the other cells of that row step instead, so
//     their size doesn't change (each new step must clear VOID_ZERO).

export type SplitResize = "none" | "changed" | "rebuilt" | "refused";

// apply a size edit of `delta` on the cell `cellId` (already set on its size) to its
// split part. "none" when the cell isn't alone in a split part, "changed" when only
// sizes moved, "rebuilt" when the tree changed, and "refused" when the edit can't be
// made (the caller undoes it). With `at`, the edit comes from a drag of the part's
// stick on that side: the cell need not be alone, just beside that stick. With `axis`,
// the edit counts only along the part's flow (the axis its size runs on).
export function resizeSplitCell(spec: MdFrameSpec, cellId: string, delta: number, at?: OutlineSide, axis?: "width" | "height"): SplitResize {
    if(delta === 0) return "none";

    const root = spec.frameBlocks;
    const path = findCellPath(root, cellId);
    if(!path || path.length < 2) return "none";

    const {block: owner, index} = path[path.length - 1];
    if(axis && (axis === "width") !== (owner.kind === "row")) return "none";

    const content = owner.children.filter(c => isCell(c) && !(c.kind === "item" && c.item.kind === "void"));
    if(!at && content.length !== 1) return "none";

    const sides: OutlineSide[] = at ? [at] : owner.kind === "column" ? ["top", "bottom"] : ["left", "right"];
    const stepped = sides.find(side => edgeOrStepStick(owner, side) && !ownEdgeStick(owner, side));
    const side = stepped ?? sides.find(side => !!ownEdgeStick(owner, side));
    if(!side || besideEdgeStickIdx(owner, side) !== index) return "none";

    const stick = edgeOrStepStick(owner, side)!;
    const voids = stepVoids(root);

    // the amount the row around the part grows by.
    let grow = delta;
    if(stepped) {
        const step = voids.get(stick.id)!;
        const size = step.kind === "void" ? step.size : undefined;
        if(!size) return "none";

        const next = size.get() - delta;
        if(next > VOID_ZERO) {
            size.set(next, {debug: "split-resize"});
            return "changed";
        }

        // the step is used up: it goes, and the rest grows the row.
        grow = -next;
        const row = grow > 0 ? rowStepsAround(spec, path, side) : {voids: [], fresh: [], chain: []};
        if(!row || !canStepRow(row, grow)) return "refused";

        removeStep(spec, step.id);
        stepRow(spec, row, side, grow);
        return "rebuilt";
    }

    if(delta < 0) {
        const size = -delta;
        if(size <= VOID_ZERO) return "refused";
        return makeStep(spec, stick.id, side, [], size, false) ? "rebuilt" : "refused";
    }

    const row = rowStepsAround(spec, path, side);
    if(!row || !canStepRow(row, grow)) return "refused";
    stepRow(spec, row, side, grow);
    return "rebuilt";
}

// the index of the cell beside a block's edge or step stick on `side` (see
// edgeOrStepStick), or -1 when it has none there.
function besideEdgeStickIdx(block: MdFrameBlock, side: OutlineSide): number {
    if(!edgeOrStepStick(block, side)) return -1;
    const stepped = !ownEdgeStick(block, side);
    return sideIsLeading(side) ? (stepped ? 2 : 1) : block.children.length - (stepped ? 3 : 2);
}

// the cell beside the own edge stick `stickId` of a split part (the cell that grows
// when the stick drags outward), with the side the stick lies on: a lite or panel, or
// a block without the door. Null when the stick isn't a block's own edge stick, or the
// cell beside it is something else.
function splitPartCell(spec: MdFrameSpec, stickId: string): {id: string; size: FrmVar; side: OutlineSide} | null {
    const own = findOwnEdgeStick(spec.frameBlocks, stickId);
    if(!own) return null;

    const beside = own.block.children[besideEdgeStickIdx(own.block, own.side)];
    if(beside?.kind === "item" && (beside.item.kind === "lite" || beside.item.kind === "fixedPanel") && beside.item.size) {
        return {id: beside.item.id, size: beside.item.size, side: own.side};
    }
    if(beside?.kind === "block" && !cellContainsDoor(beside)) {
        return {id: beside.block.id, size: beside.block.size, side: own.side};
    }
    return null;
}

// grow the split part that owns the edge stick `stickId` outward by `amount`: the cell
// beside the stick grows, and the rest of the row steps so it keeps its size (see
// resizeSplitCell).
export function growSplitPart(spec: MdFrameSpec, stickId: string, amount: number): SplitResize {
    const part = splitPartCell(spec, stickId);
    if(!part) return "none";

    const size = part.size;
    const before = size.get();
    const wasModified = size.isModified();
    size.userSet(before + amount, {debug: "split-grow"});

    const result = resizeSplitCell(spec, part.id, amount, part.side);
    if(result === "refused" || result === "none") size.restore(before, wasModified);
    return result;
}

// the lite or panel the perimeter piece `stickId` covers on `side`, when it covers just
// that one cell and the cell sits straight in a row along that side (so it can take a
// stick of its own there). Null otherwise.
function perimeterPieceCell(spec: MdFrameSpec, stickId: string, side: OutlineSide, leafIds: string[]): {row: MdFrameBlock; index: number; item: MdFrameItem; path: PathStep[]} | null {
    const loc = findPerimeterStick(spec.frameBlocks, stickId);
    if(!loc || loc.side !== side || loc.role === "threshold" || leafIds.length !== 1) return null;

    const path = findItemPath(spec.frameBlocks, leafIds[0]);
    if(!path) return null;
    const {block: row, index} = path[path.length - 1];
    const item = (row.children[index] as {kind: "item"; item: MdFrameItem}).item;
    if(item.kind !== "lite" && item.kind !== "fixedPanel") return null;
    if(row.kind === edgeOwnerKind(side)) return null;
    return {row, index, item, path};
}

// true when the perimeter piece `stickId` on `side` (covering `leafIds`) can be dragged
// outward: its one cell takes a stick of its own (see givePieceOwnStick), and then
// grows as a split part (see startSplitGrow).
export function canGrowPerimeterPiece(spec: MdFrameSpec, stickId: string, side: OutlineSide, leafIds: string[]): boolean {
    const cell = perimeterPieceCell(spec, stickId, side, leafIds);
    return !!cell && rowSteps(spec, cell.path, side) !== null;
}

// give the one cell the perimeter piece `stickId` covers a stick of its own on `side`,
// so it becomes a split part. Returns the new stick's id, or null.
export function givePieceOwnStick(spec: MdFrameSpec, stickId: string, side: OutlineSide, leafIds: string[]): string | null {
    const cell = perimeterPieceCell(spec, stickId, side, leafIds);
    const loc = findPerimeterStick(spec.frameBlocks, stickId);
    if(!cell || !loc) return null;

    const stick = newStickLike(spec, loc.stick);
    giveOwnEdgeStick(spec, cell.row, cell.index, cell.index, side, stick);
    return stick.id;
}

// how the other parts along `side` of the frame step when a split part grows across
// that side, so they keep their size: the steps they already have, and the sticks
// that must step fresh (with the cells they cover). The growth spreads up through
// each block that shares its size across `side` (a row for top/bottom, a column for
// left/right), so each such block's other cells are the parts that step.
type RowSteps = {
    voids: FrmVar[];
    fresh: {stickId: string; leaves: string[]}[];
    // the sizes along the growth of the blocks between the part and the root: they
    // grow with the part (a pinned one would hold it back).
    chain: FrmVar[];
};

// see RowSteps, for the part that owns the cell at the end of `path`.
function rowStepsAround(spec: MdFrameSpec, path: PathStep[], side: OutlineSide): RowSteps | null {
    return rowSteps(spec, path.slice(0, -1), side);
}

// see RowSteps, for the part at the end of `partPath`. Null when a part can't step
// (the door sits on its edge, or no one stick covers its edge).
function rowSteps(spec: MdFrameSpec, partPath: PathStep[], side: OutlineSide): RowSteps | null {
    const root = spec.frameBlocks;
    const voids = stepVoids(root);
    const sticks = sticksById(root);
    const out: RowSteps = {voids: [], fresh: [], chain: []};
    const shared = edgeOwnerKind(side) === "row" ? "column" : "row";

    const others: MdFrameBlockChild[] = [];
    for(const step of partPath) {
        const child = step.block.children[step.index];
        if(step.block.kind !== shared) {
            if(child.kind === "block") out.chain.push(child.block.size);
            continue;
        }
        step.block.children.forEach((c, i) => {
            if(i !== step.index && isCell(c)) others.push(c);
        });
    }

    // take in a part that must step on `side`: its step grows, or one stick covers its
    // edge and steps fresh. A block with no stick of its own there passes the growth
    // down: one that shares its size across `side` to each of its cells, one along it
    // to its cell on `side` (which grows with the part). False when a part can't step.
    const visit = (c: MdFrameBlockChild): boolean => {
        const own = c.kind === "block" ? edgeOrStepStick(c.block, side) : undefined;
        const step = own ? voids.get(own.id) : undefined;
        if(own && step?.kind === "void" && step.size) {
            // joined steps share one size: grow it once.
            if(!out.voids.includes(step.size)) out.voids.push(step.size);
            return true;
        }

        if(c.kind === "block" && !own) {
            const idx = cellIndexesOnSide(c.block, side);
            if(c.block.kind === shared) return idx.every(i => visit(c.block.children[i]));

            const edge = c.block.children[idx[0]];
            if(!edge) return false;
            if(edge.kind === "block") out.chain.push(edge.block.size);
            return visit(edge);
        }

        // one stick must cover the cell's whole edge, and step with it.
        const leaves = edgeLeafIds(c, side);
        const firstPath = leaves.length > 0 ? findItemPath(root, leaves[0]) : null;
        const cover = firstPath ? outlineCover(spec, firstPath, side) : null;
        if(!cover || !canStepFresh(spec, cover, leaves, side)) return false;
        if(!leaves.every(id => {
            const p = findItemPath(root, id);
            return !!p && coveredBy(spec, p, side, cover);
        })) return false;

        // the cells one stick covers step as one (see makeStep), even across blocks;
        // so do joined sticks, through the stick they join into.
        const rootId = joinedRootId(sticks, cover.id);
        const same = out.fresh.find(f => f.stickId === rootId);
        if(same) same.leaves.push(...leaves);
        else out.fresh.push({stickId: rootId, leaves});
        return true;
    };

    for(const c of others) {
        if(!visit(c)) return null;
    }

    return out.voids.length + out.fresh.length > 0 ? out : null;
}

// true when a step can go behind `stick` for the cells `leaves` on `side` (see
// makeStep): the cell that gives the space up isn't the door, and cells that take
// the stick as their own fall into runs.
function canStepFresh(spec: MdFrameSpec, stick: MdFrameStick, leaves: string[], side: OutlineSide): boolean {
    const root = spec.frameBlocks;
    const own = findOwnEdgeStick(root, stick.id);
    // the door keeps its size, so a fresh step beside or above it is fine; under it
    // there may be no void.
    if(own && own.side === side && !hasNestedEdgeStick(own.block, side)) {
        if(side !== "bottom") return true;
        const c = own.block.children;
        return !cellContainsDoor(sideIsLeading(side) ? c[1] : c[c.length - 2]);
    }
    return !!(side === "bottom" ? leafRuns : groupLeafRuns)(root, leaves, side);
}

// the least the row can grow by: each fresh step must clear VOID_ZERO.
function rowStepMin(row: RowSteps): number {
    return row.fresh.length === 0 ? 0 : VOID_ZERO;
}

function canStepRow(row: RowSteps, grow: number): boolean {
    return grow >= rowStepMin(row);
}

// step the row's cells by `grow` (see RowSteps). A fresh step's stick is already in
// its block's size, so the void takes the whole growth.
function stepRow(spec: MdFrameSpec, row: RowSteps, side: OutlineSide, grow: number) {
    row.voids.forEach(v => v.set(v.get() + grow, {debug: "split-resize"}));
    row.chain.forEach(v => v.set(v.get() + grow, {debug: "split-resize"}));
    row.fresh.forEach(f => makeStep(spec, f.stickId, side, f.leaves, grow, false));
}

// a live outward drag of a split part's own edge stick (see startSplitGrow).
export type SplitGrow = {
    // true when the growth needs no fresh steps, so apply() can follow the drag.
    live: boolean;
    // the least growth that can be made (fresh steps must clear VOID_ZERO).
    min: number;
    // live only: grow the part (and the row's steps) by `amount` from the start. A
    // negative amount shrinks it, down to where a step of the row reaches zero or the
    // cell reaches MIN_BLOCK.
    apply(amount: number): void;
    // live only: end the drag at the current growth.
    finish(): void;
};

// start an outward drag of the own edge stick `stickId` of a split part: its cell
// grows, and the rest of the row steps so it keeps its size. Null when the part can't
// grow (see splitPartCell, rowSteps).
export function startSplitGrow(spec: MdFrameSpec, stickId: string): SplitGrow | null {
    const part = splitPartCell(spec, stickId);
    if(!part) return null;

    const path = findCellPath(spec.frameBlocks, part.id);
    const row = path ? rowStepsAround(spec, path, part.side) : null;
    if(!row) return null;

    const size = part.size;
    const before = size.get();
    const wasModified = size.isModified();
    const voidsBefore = row.voids.map(v => v.get());
    const chainBefore = row.chain.map(v => v.get());
    const floor = -Math.min(...voidsBefore, before - MIN_BLOCK);

    return {
        live: row.fresh.length === 0,
        min: rowStepMin(row),
        apply(amount: number) {
            amount = Math.max(floor, amount);
            size.userSet(before + amount, {debug: "split-grow"});
            row.voids.forEach((v, i) => v.set(voidsBefore[i] + amount, {debug: "split-grow"}));
            row.chain.forEach((v, i) => v.set(chainBefore[i] + amount, {debug: "split-grow"}));
        },
        // a drag sizes the cell, it doesn't lock it: drop the pin the drag added.
        finish() {
            if(!wasModified) size.clearModified();
        },
    };
}

// ---- sloped sticks --------------------------------------------------------------
// An outline stick may slope (MdFrameStick.slope): the cells under it are `drop`
// shorter at the short end than at the other. Only a block's own edge stick that
// covers no door can, and not one joined with another. The layout stays rectangular:
// the cell's size is the tall end, and the slope is drawn inside it (see Slope.ts).

const DEFAULT_DROP = 6 * inches;

// the cells along the edge under the own edge stick `stickId` (the cells a slope cuts).
function stickLeafIds(spec: MdFrameSpec, stickId: string): string[] {
    const own = findOwnEdgeStick(spec.frameBlocks, stickId);
    if(!own) return [];
    const beside = own.block.children[besideEdgeStickIdx(own.block, own.side)];
    return beside ? edgeLeafIds(beside, own.side) : [];
}

// the most the cells under `stickId` can drop and keep MIN_BLOCK at the short end, or
// null when the stick can't be sloped.
function slopedLimit(spec: MdFrameSpec, stickId: string): number | null {
    const stick = findStickById(spec, stickId);
    const own = stick ? findOwnEdgeStick(spec.frameBlocks, stickId) : null;
    if(!stick || !own) return null;

    const sticks = sticksById(spec.frameBlocks);
    if(stick.joinedTo || Array.from(sticks.values()).some(s => s.joinedTo === stickId)) return null;

    const leaves = stickLeafIds(spec, stickId);
    if(leaves.length === 0) return null;
    if(leaves.some(id => {
        const kind = findItem(spec, id)?.kind;
        return kind === "doors" || kind === "threshold" || kind === "void";
    })) return null;

    // before the first layout no size is known: nothing to limit yet.
    const across = own.side === "top" || own.side === "bottom" ? "height" : "width";
    const sizes = leaves.map(id => spec.sizeInfo.get(id)?.[across]).filter((n): n is number => n !== undefined);
    if(sizes.length === 0) return Infinity;
    return Math.min(...sizes) - MIN_BLOCK;
}

// the sticks that meet `stickId` end to end along its edge, by the end of `stickId`
// they meet: A at the start of the edge (left or top), B at the end. A step beside
// the stick is a corner with the step's stick, not a neighbour along the edge.
function sticksAlongEdge(spec: MdFrameSpec, stickId: string): Map<"A" | "B", MdFrameStick> {
    const out = new Map<"A" | "B", MdFrameStick>();
    const own = findOwnEdgeStick(spec.frameBlocks, stickId);
    if(!own) return out;

    const root = spec.frameBlocks;
    const order = edgeLeafIds({kind: "block", block: root}, own.side);
    const mine = stickLeafIds(spec, stickId).map(id => order.indexOf(id)).filter(i => i >= 0);
    if(mine.length === 0) return out;

    const coverAt = (idx: number): MdFrameStick | null => {
        const id = order[idx];
        if(id === undefined || findItem(spec, id)?.kind === "void") return null;
        const path = findItemPath(root, id);
        return path ? outlineCover(spec, path, own.side) : null;
    };
    const before = coverAt(Math.min(...mine) - 1);
    if(before && before.id !== stickId) out.set("A", before);
    const after = coverAt(Math.max(...mine) + 1);
    if(after && after.id !== stickId) out.set("B", after);
    return out;
}

// the ends of `stickId` that may be its short end. A stick meeting it along the edge
// sits at the cells' full size, so that end must be the tall end: a straight
// neighbour continues level from it, a sloped one meets it tall end to tall end (a
// peak). Empty when neither end can be short: sticks at both ends, or a sloped
// neighbour with its short end at the meeting point (flip it first).
function allowedShortEnds(spec: MdFrameSpec, stickId: string): Set<"A" | "B"> {
    const out = new Set<"A" | "B">(["A", "B"]);
    for(const [end, near] of sticksAlongEdge(spec, stickId)) {
        out.delete(end);
        const meetingEnd = end === "A" ? "B" : "A";
        if(near.slope?.shortEnd === meetingEnd) out.clear();
    }
    return out;
}

// the short end `stickId` takes when sloped: B unless a neighbour along the edge
// rules it out (see allowedShortEnds). Null when neither end can be short.
function peakShortEnd(spec: MdFrameSpec, stickId: string): "A" | "B" | null {
    const allowed = allowedShortEnds(spec, stickId);
    return allowed.has("B") ? "B" : allowed.has("A") ? "A" : null;
}

export function canMakeSloped(spec: MdFrameSpec, stickId: string): boolean {
    const limit = slopedLimit(spec, stickId);
    return limit !== null && limit >= inches && peakShortEnd(spec, stickId) !== null;
}

// slope `stickId`: its cells drop by DEFAULT_DROP (or what MIN_BLOCK leaves) at end B,
// or at the end away from a neighbour along the edge (see peakShortEnd).
export function makeSloped(spec: MdFrameSpec, stickId: string): boolean {
    const stick = findStickById(spec, stickId);
    const limit = slopedLimit(spec, stickId);
    const shortEnd = peakShortEnd(spec, stickId);
    if(!stick || limit === null || limit < inches || !shortEnd) return false;

    const drop = new FrmVar(nextId(spec, "drop"), 0);
    drop.userSet(Math.min(DEFAULT_DROP, limit), {debug: "make-sloped"});
    stick.slope = {shortEnd, drop};
    return true;
}

export function removeSlope(spec: MdFrameSpec, stickId: string): boolean {
    const stick = findStickById(spec, stickId);
    if(!stick?.slope) return false;
    stick.slope = undefined;
    return true;
}

// true when the short end of a sloped stick can swap: not while a stick meets it
// along the edge at the other end (see allowedShortEnds).
export function canFlipSlope(spec: MdFrameSpec, stickId: string): boolean {
    const stick = findStickById(spec, stickId);
    if(!stick?.slope) return false;
    return allowedShortEnds(spec, stickId).has(stick.slope.shortEnd === "A" ? "B" : "A");
}

// swap the short end of a sloped stick.
export function flipSlope(spec: MdFrameSpec, stickId: string): boolean {
    const stick = findStickById(spec, stickId);
    if(!stick?.slope || !canFlipSlope(spec, stickId)) return false;
    stick.slope.shortEnd = stick.slope.shortEnd === "A" ? "B" : "A";
    return true;
}

// set the drop of a sloped stick, kept within what MIN_BLOCK at the short end
// allows. False when the stick isn't sloped.
export function setSlopeDrop(spec: MdFrameSpec, stickId: string, drop: number): boolean {
    const stick = findStickById(spec, stickId);
    const limit = slopedLimit(spec, stickId);
    if(!stick?.slope || limit === null) return false;
    stick.slope.drop.userSet(Math.max(0, Math.min(drop, limit)), {debug: "slope-drop"});
    return true;
}

// a number edit of a cell's short end under a sloped stick: the cell, the axis it
// slopes across, and the short end asked for.
export type ShortEndEdit = {stickId: string; cellId: string; axis: "width" | "height"; value: number};

// set the drop so the short end of the edit's cell reads its value with the cell's
// last-layout size. A deeper drop lays the stock steeper, so the slot it takes grows
// and a cell that can't grow (its row pinned) loses a little at the tall end: run
// again after each layout until the drop settles. True when the drop moved.
export function applyShortEndEdit(spec: MdFrameSpec, edit: ShortEndEdit): boolean {
    const stick = findStickById(spec, edit.stickId);
    const size = spec.sizeInfo.get(edit.cellId)?.[edit.axis];
    if(!stick?.slope || size === undefined) return false;
    const before = stick.slope.drop.get();
    if(!setSlopeDrop(spec, edit.stickId, size - edit.value)) return false;
    return Math.abs(stick.slope.drop.get() - before) > 1e-6;
}

// a number edit of a cell's size under a sloped stick, kept until the layout has run:
// the stick, the axis it slopes across, the last-layout size of each cell under it, and
// the drop to flip to (null when the slope keeps its direction).
export type TallEndEdit = {stickId: string; axis: "width" | "height"; sizes: Map<string, number>; flipDrop: number | null};

// start a number edit that moved the cell `cellId`'s `size` on `axis` by `delta`. Under a
// sloped stick across that axis only the tall end moves, so the short end stays. A tall
// end set under the short end flips the slope: the cell takes the old short end's size,
// and the edited end becomes the short end. Returns the edit and the delta the size
// really moved by; null when no sloped stick runs across the axis, and "refused" when
// the new short end would be under MIN_BLOCK.
export function beginTallEndEdit(spec: MdFrameSpec, cellId: string, axis: "width" | "height", size: FrmVar, delta: number): {edit: TallEndEdit; delta: number} | "refused" | null {
    const sloped = slopesOver(spec, cellId);
    const stick = axis === "height"
        ? sloped.get("top") ?? sloped.get("bottom")
        : sloped.get("left") ?? sloped.get("right");
    if(!stick?.slope) return null;

    const sizes = new Map<string, number>();
    for(const id of stickLeafIds(spec, stick.id)) {
        const before = spec.sizeInfo.get(id)?.[axis];
        if(before !== undefined) sizes.set(id, before);
    }

    const value = size.get();
    const before = value - delta;
    const short = before - stick.slope.drop.get();
    if(value >= short) {
        return {edit: {stickId: stick.id, axis, sizes, flipDrop: null}, delta};
    }

    if(value < MIN_BLOCK) return "refused";
    size.set(short, {debug: "slope-flip"});
    return {edit: {stickId: stick.id, axis, sizes, flipDrop: short - value}, delta: short - before};
}

// finish a TallEndEdit once the layout has run: the drop takes how far the cells under
// the stick grew, so the short end stays (or the slope flips). True when the slope
// changed and the grid must lay out again.
export function finishTallEndEdit(spec: MdFrameSpec, edit: TallEndEdit): boolean {
    const stick = findStickById(spec, edit.stickId);
    if(!stick?.slope) return false;

    if(edit.flipDrop !== null) {
        stick.slope.shortEnd = stick.slope.shortEnd === "A" ? "B" : "A";
        stick.slope.drop.userSet(edit.flipDrop, {debug: "slope-flip"});
        return true;
    }

    // a cell that grew less than the others keeps its short end.
    const grown = Array.from(edit.sizes, ([id, before]) => (spec.sizeInfo.get(id)?.[edit.axis] ?? before) - before);
    const growth = grown.length > 0 ? Math.min(...grown) : 0;
    if(growth === 0) return false;

    stick.slope.drop.userSet(Math.max(0, stick.slope.drop.get() + growth), {debug: "slope-tall-end"});
    return true;
}

// keep every slope within the rules after a change elsewhere: a stick that can't be
// sloped any more (stepped, joined, over the door, or its short end now against a stick
// along the edge) goes straight, and a drop beyond what MIN_BLOCK at the short end
// allows is cut back. Returns true when one changed.
export function normalizeSlopes(spec: MdFrameSpec): boolean {
    let changed = false;
    for(const stick of sticksById(spec.frameBlocks).values()) {
        if(!stick.slope) continue;
        const limit = slopedLimit(spec, stick.id);
        if(limit === null || limit <= 0 || !allowedShortEnds(spec, stick.id).has(stick.slope.shortEnd)) {
            stick.slope = undefined;
            changed = true;
        } else if(stick.slope.drop.get() > limit) {
            stick.slope.drop.set(limit, {debug: "normalize-slopes"});
            changed = true;
        }
    }
    return changed;
}

// the sloped sticks over the cell `cellId` (an item or a block), by the side of the cell
// they lie on.
export function slopesOver(spec: MdFrameSpec, cellId: string): Map<OutlineSide, MdFrameStick> {
    const out = new Map<OutlineSide, MdFrameStick>();
    const path = findCellPath(spec.frameBlocks, cellId);
    if(!path) return out;
    for(const [side, stick] of outlineCovers(spec, path)) {
        if(stick.slope) out.set(side, stick);
    }
    return out;
}

// ---- angled sticks (plan view) ---------------------------------------------------
// A double-sided floor-to-ceiling mullion may turn in plan (MdFrameStick.angle, in
// degrees): the cells on its right leave the cells on its left at that angle, measured
// on the locationOne side. Only an interior stick of the row the top-down section draws
// (see doorTopLevelRow) with a cell on each side can, and not one drawn in pieces beside
// a step. The elevation keeps drawing the frame flat; the top-down section turns.

export const STRAIGHT_ANGLE_DEG = 180;
const DEFAULT_ANGLE_DEG = 90;
export const MIN_ANGLE_DEG = 1;
export const MAX_ANGLE_DEG = 359;

export function clampAngleDeg(deg: number): number {
    return Math.max(MIN_ANGLE_DEG, Math.min(MAX_ANGLE_DEG, deg));
}

// true when `stickId` is an interior mullion of the top-down row with a cell on each
// side, neither of which steps beside it.
function isTurnableStick(spec: MdFrameSpec, stickId: string): boolean {
    const row = doorTopLevelRow(spec);
    if(!row) return false;
    const index = row.children.findIndex(c => c.kind === "stick" && c.stick.id === stickId);
    if(index <= 0 || index >= row.children.length - 1) return false;
    const left = row.children[index - 1];
    const right = row.children[index + 1];
    if(!isCell(left) || !isCell(right)) return false;
    return !edgeHasVoid(left, "right") && !edgeHasVoid(right, "left");
}

export function canMakeAngled(spec: MdFrameSpec, stickId: string): boolean {
    const stick = findStickById(spec, stickId);
    return !!stick && !stick.angle && !stick.airGap && isTurnableStick(spec, stickId);
}

// turn the run at `stickId` by DEFAULT_ANGLE_DEG.
export function makeAngled(spec: MdFrameSpec, stickId: string): boolean {
    const stick = findStickById(spec, stickId);
    if(!stick || stick.angle || stick.airGap || !isTurnableStick(spec, stickId)) return false;

    const angle = new FrmVar(stick.id + ":angle", STRAIGHT_ANGLE_DEG);
    angle.userSet(DEFAULT_ANGLE_DEG, {debug: "make-angled"});
    stick.angle = angle;
    return true;
}

export function removeAngle(spec: MdFrameSpec, stickId: string): boolean {
    const stick = findStickById(spec, stickId);
    if(!stick?.angle) return false;
    stick.angle = undefined;
    return true;
}

// set the plan angle of an angled stick, kept within MIN_ANGLE_DEG..MAX_ANGLE_DEG.
// False when the stick isn't angled.
export function setStickAngle(spec: MdFrameSpec, stickId: string, deg: number): boolean {
    const stick = findStickById(spec, stickId);
    if(!stick?.angle || isNaN(deg)) return false;
    stick.angle.userSet(clampAngleDeg(deg), {debug: "stick-angle"});
    return true;
}

// the turn of the run at `stick` off straight, in radians: positive turns toward the
// locationOne side (the bottom of the top-down section). 0 for a straight stick.
export function stickTurnRad(stick: MdFrameStick): number {
    if(!stick.angle) return 0;
    return (STRAIGHT_ANGLE_DEG - stick.angle.get()) * Math.PI / 180;
}

// drop the angle of a stick that can't turn any more (no longer an interior mullion
// of the top-down row, or a cell beside it stepped). Returns true when one changed.
export function normalizeAngles(spec: MdFrameSpec): boolean {
    let changed = false;
    for(const stick of sticksById(spec.frameBlocks).values()) {
        if(!stick.angle || isTurnableStick(spec, stick.id)) continue;
        stick.angle = undefined;
        changed = true;
    }
    return changed;
}

// ---- air gaps -----------------------------------------------------------------------

function isAirGap(c: MdFrameBlockChild | undefined): boolean {
    return c?.kind === "stick" && !!c.stick.airGap;
}

// true when `stickId` is an interior stick with a cell on each side, neither a void
// nor stepping beside it, and (for a vertical stick) neither holding the door: the
// door's jambs stay stock.
function isAirGapCandidate(spec: MdFrameSpec, stickId: string): boolean {
    const path = findStickPath(spec.frameBlocks, stickId);
    if(!path) return false;

    const before = path.block.children[path.index - 1];
    const after = path.block.children[path.index + 1];
    if(!isCell(before) || !isCell(after)) return false;

    const vertical = path.block.kind === "row";
    if(vertical && (cellContainsDoor(before) || cellContainsDoor(after))) return false;

    return !edgeHasVoid(before, vertical ? "right" : "bottom") && !edgeHasVoid(after, vertical ? "left" : "top");
}

export function canMakeAirGap(spec: MdFrameSpec, stickId: string): boolean {
    const stick = findStickById(spec, stickId);
    return !!stick && !stick.airGap && isAirGapCandidate(spec, stickId);
}

// make the stick an air gap of DEFAULT_AIR_GAP. Its angle and profile overrides go
// (there is no stock to turn or profile); the thickness change goes to the cells
// beside it.
export function makeAirGap(spec: MdFrameSpec, stickId: string): boolean {
    const stick = findStickById(spec, stickId);
    if(!stick || stick.airGap || !isAirGapCandidate(spec, stickId)) return false;

    stick.airGap = true;
    stick.angle = undefined;
    stick.profileLeft = undefined;
    stick.profileRight = undefined;
    setStickThickness(spec, stickId, DEFAULT_AIR_GAP);
    return true;
}

// put frame stock back in an air gap, at the spec's jamb face.
export function makeMullion(spec: MdFrameSpec, stickId: string): boolean {
    const stick = findStickById(spec, stickId);
    if(!stick?.airGap) return false;

    stick.airGap = undefined;
    setStickThickness(spec, stickId, spec.jambFace.get());
    return true;
}

// set a stick's thickness (an air gap's gap), kept at or above MIN_AIR_GAP on an air
// gap. The change goes to the cells beside the stick the way a thickness edit on the
// canvas does (see makeMullionThicknessAbsorb). False when the stick isn't found.
export function setStickThickness(spec: MdFrameSpec, stickId: string, value: number): boolean {
    const stick = findStickById(spec, stickId);
    const path = findStickPath(spec.frameBlocks, stickId);
    if(!stick || !path || isNaN(value)) return false;

    const next = stick.airGap ? Math.max(MIN_AIR_GAP, value) : value;
    const thickness = stickThicknessVar(stick, spec);
    const delta = next - thickness.get();
    if(delta === 0) return true;

    thickness.userSet(next, {debug: "stick-thickness"});
    makeMullionThicknessAbsorb({
        leading: () => absorbingSizeVar(path.block.children[path.index - 1]),
        trailing: () => absorbingSizeVar(path.block.children[path.index + 1]),
    })(delta);
    return true;
}

// the flow-axis size var of a cell that can give space to a thickness change, or null
// for a cell that can't (a door-bearing one). A cell with no size var of its own is
// unpinned, so a fresh (unmodified) var stands in for it.
function absorbingSizeVar(c: MdFrameBlockChild | undefined): FrmVar | null {
    if(!c || cellContainsDoor(c)) return null;
    if(c.kind === "block") return c.block.size;
    if(c.kind !== "item" || c.item.kind === "doors") return null;
    return c.item.size ?? new FrmVar(c.item.id + ":unsized", 0);
}

// drop the air gap of a stick that can no longer be one (no longer between two cells,
// or a cell beside it stepped or now holds the door's leaf): it is stock again at the
// jamb face. Returns true when one changed.
export function normalizeAirGaps(spec: MdFrameSpec): boolean {
    let changed = false;
    for(const stick of sticksById(spec.frameBlocks).values()) {
        if(!stick.airGap || isAirGapCandidate(spec, stick.id)) continue;
        makeMullion(spec, stick.id);
        changed = true;
    }
    return changed;
}

// ---- canonical form -----------------------------------------------------------
// Every structural edit builds a locally correct tree; the simplest tree that draws the
// same frame is found afterwards by this pass, so no edit has to look at its
// neighbours. It runs after each change (FrameGridView.render, not during a drag) and
// repeats its rules until nothing changes:
//   1. sibling wrappers that each hold one cell and an edge stick on the same side,
//      with those sticks drawn as one (joined), merge into one wrapper over the run.
//   2. a block of its parent's kind is flattened into the parent.
//   3. a single-cell wrapper with no sticks collapses (collapseWrappers).
// Returns whether the tree changed, and the sticks the merges dropped with the stick
// that took their place (hardware mounted on them re-homes).
export function simplifyTree(spec: MdFrameSpec): {changed: boolean; replacedSticks: Map<string, string>} {
    const replacedSticks = new Map<string, string>();
    const before = treeSignature(spec.frameBlocks);
    let last = before;

    for(let i = 0; i < 50; i++) {
        mergeSiblingWrappers(spec, replacedSticks);
        collapseWrappers(spec);
        flattenSameKind(spec.frameBlocks);

        const now = treeSignature(spec.frameBlocks);
        if(now === last) break;
        last = now;
    }

    return {changed: last !== before, replacedSticks};
}

// the tree's structure as text: block kinds and ids, child order.
function treeSignature(block: MdFrameBlock): string {
    return block.kind + "(" + block.id + ")[" + block.children.map(c =>
        c.kind === "item" ? c.item.id :
        c.kind === "stick" ? "|" + c.stick.id :
        treeSignature(c.block)).join(",") + "]";
}

// a block that holds exactly one cell and one edge stick: the stick and its side.
function plainWrapper(c: MdFrameBlockChild | undefined): {stick: MdFrameStick; side: OutlineSide} | null {
    if(c?.kind !== "block" || c.block.children.length !== 2) return null;
    for(const side of ["top", "bottom", "left", "right"] as OutlineSide[]) {
        const stick = ownEdgeStick(c.block, side);
        if(stick) return {stick, side};
    }
    return null;
}

// rule 1, once per call for the first pair found: two neighbouring plain wrappers in a
// block, on the same side, whose sticks draw as one, become one wrapper
// (joinSiblingOwners). The stick the pair draws as (the join root) survives when it is
// one of the two, else the first; the other's id maps to it.
function mergeSiblingWrappers(spec: MdFrameSpec, replaced: Map<string, string>): boolean {
    const walk = (block: MdFrameBlock): boolean => {
        for(let i = 0; i + 2 < block.children.length; i++) {
            const a = plainWrapper(block.children[i]);
            const between = block.children[i + 1];
            const b = plainWrapper(block.children[i + 2]);
            if(!a || !b || between.kind !== "stick" || a.side !== b.side) continue;
            if(drawnStick(spec, a.stick) !== drawnStick(spec, b.stick)) continue;

            const keepFirst = drawnStick(spec, b.stick) !== b.stick;
            const [keep, drop] = keepFirst ? [a.stick, b.stick] : [b.stick, a.stick];
            const keepOwner = findEdgeOwner(spec, keep.id);
            const dropOwner = findEdgeOwner(spec, drop.id);
            if(!keepOwner || !dropOwner) continue;

            const dropNext = drop.joinedTo === keep.id ? undefined : drop.joinedTo;
            joinSiblingOwners(spec, keepOwner, dropOwner);
            for(const s of sticksById(spec.frameBlocks).values()) {
                if(s.joinedTo !== drop.id) continue;
                s.joinedTo = s === keep ? dropNext : keep.id;
            }
            replaced.set(drop.id, keep.id);
            return true;
        }
        return block.children.some(c => c.kind === "block" && walk(c.block));
    };
    return walk(spec.frameBlocks);
}

// rule 2: a child block of its parent's kind is spliced into the parent, where the
// cell/stick alternation holds across its ends.
function flattenSameKind(block: MdFrameBlock): boolean {
    let changed = false;
    for(let i = 0; i < block.children.length; i++) {
        const c = block.children[i];
        if(c.kind !== "block") continue;
        if(flattenSameKind(c.block)) changed = true;
        if(c.block.kind !== block.kind || c.block.children.length === 0) continue;

        const inner = c.block.children;
        const prev = block.children[i - 1];
        const next = block.children[i + 1];
        if(prev && isCell(prev) === isCell(inner[0])) continue;
        if(next && isCell(next) === isCell(inner[inner.length - 1])) continue;

        block.children.splice(i, 1, ...inner);
        changed = true;
        i += inner.length - 1;
    }
    return changed;
}

// no side of the frame outline may be void all the way along: where every leaf cell on
// a side is a void, the steps on that side shrink by the smallest of them, so one
// reaches the edge. A step at or under VOID_ZERO goes: its stick goes back on the edge,
// and the cell beside it takes what was left of the void, so the block keeps its size.
// Returns true when a step went (the tree changed).
export function normalizeSteps(spec: MdFrameSpec): boolean {
    const root = spec.frameBlocks;
    const voids = stepVoids(root);
    const zero = new Set<FrmVar>();

    for(const side of ["top", "bottom", "left", "right"] as OutlineSide[]) {
        const leaves = edgeLeafIds({kind: "block", block: root}, side);
        if(leaves.length === 0 || !leaves.every(id => findItem(spec, id)?.kind === "void")) continue;

        // a void that steps another side only reaches this one at a corner, so it
        // can't bring this edge back: leave it out of the shrink.
        const sizes: FrmVar[] = [];
        for(const id of leaves) {
            const size = stepSizeOnSide(root, id, side);
            if(size && !sizes.includes(size)) sizes.push(size);
        }
        if(sizes.length === 0) continue;

        const min = Math.min(...sizes.map(v => v.get()));
        if(min > 0) sizes.forEach(v => v.set(v.get() - min, {debug: "normalize-steps"}));
    }

    for(const item of voids.values()) {
        if(item.kind === "void" && item.size && item.size.get() <= VOID_ZERO) zero.add(item.size);
    }

    let removed = false;
    for(const [stickId, item] of voids) {
        if(item.kind !== "void" || !item.size || !zero.has(item.size)) continue;

        const path = findItemPath(root, item.id);
        if(!path) continue;
        const {block, index} = path[path.length - 1];
        const beside = block.children[index === 0 ? 2 : index - 2];
        shrinkCell(beside, -item.size.get());
        block.children.splice(index, 1);
        removed = true;
    }
    return removed;
}

// no own edge stick spans an open leaf (see openLeaf): the frame draws no stick along
// a void's outer edges nor under the door's sill, so the model holds none there. A
// stick that would moves down onto the cells it still covers (joined sticks when they
// sit in different blocks, see giveRunsSticks); one that covers nothing else goes.
// Returns true when the tree changed.
export function normalizeEdgeSticks(spec: MdFrameSpec): boolean {
    const skip = new Set<string>();
    let changed = false;

    for(let i = 0; i < 50; i++) {
        const hit = stickOverOpenLeaf(spec, skip);
        if(!hit) break;

        const at = sideIsLeading(hit.side) ? 0 : hit.block.children.length - 1;
        if(hit.keep.length === 0) {
            hit.block.children.splice(at, 1);
        } else if(!moveOwnStickDown(spec, hit.block, hit.side, hit.keep, true)) {
            // the cells fit no runs: the stick stays, and the next pass tries again.
            skip.add(hit.stick.id);
            continue;
        }
        changed = true;
    }
    return changed;
}

// a leaf cell along `side` of the frame outline that no stick covers: a void, or the
// threshold on the bottom (it is the door's sill itself).
function openLeaf(spec: MdFrameSpec, leafId: string, side: OutlineSide): boolean {
    const kind = findItem(spec, leafId)?.kind;
    return kind === "void" || (kind === "threshold" && side === "bottom");
}

// the first own edge stick in the tree that covers an open leaf on the frame outline,
// with the leaves it covers that aren't open, in edge order. Null when there is none
// (outside `skip`).
function stickOverOpenLeaf(spec: MdFrameSpec, skip: Set<string>): {block: MdFrameBlock; side: OutlineSide; stick: MdFrameStick; keep: string[]} | null {
    const root = spec.frameBlocks;

    const check = (block: MdFrameBlock, side: OutlineSide) => {
        const stick = ownEdgeStick(block, side);
        if(!stick || skip.has(stick.id)) return null;

        const covered = edgeLeafIds({kind: "block", block}, side).filter(id => {
            const path = findItemPath(root, id);
            return !!path && outlineCover(spec, path, side) === stick;
        });
        if(!covered.some(id => openLeaf(spec, id, side))) return null;
        return {block, side, stick, keep: covered.filter(id => !openLeaf(spec, id, side))};
    };

    const walk = (block: MdFrameBlock): ReturnType<typeof check> => {
        for(const side of ["top", "bottom", "left", "right"] as OutlineSide[]) {
            const hit = check(block, side);
            if(hit) return hit;
        }
        for(const c of block.children) {
            if(c.kind !== "block") continue;
            const hit = walk(c.block);
            if(hit) return hit;
        }
        return null;
    };
    return walk(root);
}

// the size of the step the void `voidId` makes on `side`: the void sits at that end of
// a block whose flow crosses the side, behind its step stick. Undefined when the void
// steps another side.
function stepSizeOnSide(root: MdFrameBlock, voidId: string, side: OutlineSide): FrmVar | undefined {
    const path = findItemPath(root, voidId);
    if(!path) return undefined;

    const {block, index} = path[path.length - 1];
    if(block.kind !== edgeOwnerKind(side)) return undefined;
    if(index !== (sideIsLeading(side) ? 0 : block.children.length - 1)) return undefined;

    const c = block.children[index];
    const stick = block.children[sideIsLeading(side) ? 1 : index - 1];
    if(c.kind !== "item" || c.item.kind !== "void" || stick?.kind !== "stick") return undefined;
    return c.item.size;
}

// remove a step: drop the void and put the stick in front of it back on the frame
// edge. False when the id isn't a void at the end of its block behind a stick.
export function removeStep(spec: MdFrameSpec, voidId: string): boolean {
    const root = spec.frameBlocks;
    const voids = stepVoids(root);
    const stickId = Array.from(voids.entries()).find(([, item]) => item.id === voidId)?.[0];
    if(!stickId) return false;

    // the steps joined with this one go with it, so the group stays one stick.
    const sticks = sticksById(root);
    const group = joinedRootId(sticks, stickId);
    for(const [id, item] of voids) {
        if(joinedRootId(sticks, id) !== group) continue;

        const path = findItemPath(root, item.id);
        if(!path) continue;
        const {block, index} = path[path.length - 1];
        block.children.splice(index, 1);
    }
    return true;
}

// ---- cross flip (transpose) -------------------------------------------------
// Flip a cross: the through stick `throughStickId` (a parent-level interior
// mullion) currently runs through while perpendicular sticks cap into it. Flipping
// makes the perpendicular run continuous and splits the through stick, which means
// re-nesting (transposing) the through stick's parent block so "children cap into
// parents" still holds.
//
// `crossLines` are the aligned capping-stick pairs (from runtime detection); each
// pair has one stick in the left neighbour cell and one in the right. We split both
// neighbour cells at those lines and rebuild as the opposite orientation. Operates
// only on the through stick's parent block (transposed in place when that block is
// exactly [L, S, R]; otherwise the [L,S,R] run is wrapped in a new block).
// `keepId` (optional) is a stick the caller wants to survive the flip so it can
// stay selected — the through stick (split, kept by default as a capping segment)
// or a capping stick being extended (kept as the merged through instead of the
// left-side default).
export function flipCross(spec: MdFrameSpec, throughStickId: string, crossLines: MdCrossLine[], keepId?: string): boolean {
    if(crossLines.length === 0) return false;

    // the capping stick across the cross from a given left-side split stick.
    const partnerOf = (id: string): string | undefined => {
        for(const cl of crossLines) {
            if(cl.idA === id) return cl.idB;
            if(cl.idB === id) return cl.idA;
        }
        return undefined;
    };

    const path = findStickPath(spec.frameBlocks, throughStickId);
    if(!path) return false;

    const P = path.block;
    const sIdx = path.index;
    const rowKind = P.kind;                       // the through stick's parent flow
    const colKind: "row" | "column" = rowKind === "row" ? "column" : "row";

    const leftChild = P.children[sIdx - 1];
    const rightChild = P.children[sIdx + 1];
    if(!leftChild || !rightChild) return false;
    if(leftChild.kind !== "block" || rightChild.kind !== "block") return false;

    const Lblock = leftChild.block;
    const Rblock = rightChild.block;
    // neighbours must be the opposite orientation (they hold the perpendicular
    // capping sticks); otherwise this isn't a transposable cross.
    if(Lblock.kind !== colKind || Rblock.kind !== colKind) return false;

    // sort each cross line's two sticks into the left vs right neighbour.
    const leftIds = new Set<string>();
    const rightIds = new Set<string>();
    for(const cl of crossLines) {
        const aInL = directStick(Lblock, cl.idA);
        const inLeft = aInL ? cl.idA : cl.idB;
        const inRight = aInL ? cl.idB : cl.idA;
        if(!directStick(Lblock, inLeft) || !directStick(Rblock, inRight)) return false;
        leftIds.add(inLeft);
        rightIds.add(inRight);
    }

    const segL = splitAtSticks(Lblock, leftIds);
    const segR = splitAtSticks(Rblock, rightIds);
    if(segL.segments.length !== segR.segments.length) return false;          // misaligned
    if(segL.segments.some(s => s.length === 0) || segR.segments.some(s => s.length === 0)) return false;

    // seed sizes on the new blocks from the pre-flip layout (spec.sizeInfo) so the
    // solver starts near the old footprint instead of jumping. `wrapAxis` is the
    // through stick's parent-flow axis (the footprint the wrapper occupies);
    // `segAxis` is the perpendicular — each band's stacking extent.
    const sChild = P.children[sIdx];
    if(sChild.kind !== "stick") return false;
    const sThick = stickThicknessVar(sChild.stick, spec).get();

    const wrapAxis: "width" | "height" = rowKind === "row" ? "width" : "height";
    const segAxis: "width" | "height" = wrapAxis === "width" ? "height" : "width";

    const sizeOf = (id: string, axis: "width" | "height") => spec.sizeInfo.get(id)?.[axis] ?? 0;
    const segExtent = (seg: MdFrameBlockChild[], axis: "width" | "height") => {
        let total = 0;
        for(const c of seg) {
            if(c.kind === "item") total += sizeOf(c.item.id, axis);
            else if(c.kind === "block") total += sizeOf(c.block.id, axis);
            else if(c.kind === "stick") total += stickThicknessVar(c.stick, spec).get();
        }
        return total;
    };
    const wrapExtent = sizeOf(Lblock.id, wrapAxis) + sThick + sizeOf(Rblock.id, wrapAxis);

    // a single-cell side becomes a direct child of the new rowSeg, so its flow axis
    // flips from the neighbour block's (colKind) to the through stick's parent flow
    // (wrapAxis). Its size var still holds the pre-flip (colKind) extent — read on the
    // new axis it means the wrong thing (e.g. a sidelite's pinned width collapses its
    // height). Re-seed it from the pre-flip layout on wrapAxis, keeping the pinned/soft
    // state. A door carries its own width+height, and a fresh multi-cell wrapper has no
    // sizeInfo entry (its size is already seeded from the layout), so both are skipped.
    // re-seed a flow var, keeping its pinned (userSet) vs soft (set) state.
    const reseedVar = (v: FrmVar, value: number) => {
        if(v.isModified()) v.userSet(value, {debug: "cross-flip"});
        else v.set(value, {debug: "cross-flip"});
    };
    const reseedFlow = (child: MdFrameBlockChild) => {
        if(child.kind === "stick") return;
        if(child.kind === "item" && child.item.kind === "doors") return;

        const id = child.kind === "item" ? child.item.id : child.block.id;
        if(!spec.sizeInfo.get(id)) return;
        const target = sizeOf(id, wrapAxis);

        if(child.kind === "item") {
            const item = child.item;
            if(item.kind === "doors") return;
            if(!item.size) { item.size = new FrmVar(id + ":size", target); return; }
            reseedVar(item.size, target);
            return;
        }
        reseedVar(child.block.size, target);
    };

    const segCount = segL.segments.length;
    const wChildren: MdFrameBlockChild[] = [];
    for(let i = 0; i < segCount; i++) {
        // first row reuses the through stick's id; the rest get fresh ids.
        const vertId = i === 0 ? throughStickId : nextId(spec, "mullion");
        const left = unwrapSegment(spec, segL.segments[i], colKind, sizeOf(Lblock.id, wrapAxis));
        const right = unwrapSegment(spec, segR.segments[i], colKind, sizeOf(Rblock.id, wrapAxis));
        reseedFlow(left);
        reseedFlow(right);

        // a rowSeg carrying the door must not flex on segAxis — pin its size to the
        // door's cross extent so the door keeps its exact width and the lite/panel
        // stacked with it flexes to match. Otherwise seed soft from one side's extent
        // (the sides share segAxis) and let the solver share the run.
        const door = [...segL.segments[i], ...segR.segments[i]].find(c => c.kind === "item" && c.item.kind === "doors");
        const size = new FrmVar(nextId(spec, "block-size"), door && door.kind === "item" ? sizeOf(door.item.id, segAxis) : segExtent(segL.segments[i], segAxis));
        if(door) size.markModified();

        const rowSeg: MdFrameBlock = {
            id: nextId(spec, "block"),
            kind: rowKind,
            size,
            children: [
                left,
                {kind: "stick", stick: {id: vertId}},
                right,
            ],
        };
        wChildren.push({kind: "block", block: rowSeg});

        // the merged through stick between rows reuses the left split stick's id —
        // unless the caller extended the right-side partner and wants that id kept.
        if(i < segCount - 1) {
            const lid = segL.splitSticks[i].id;
            const mergedId = keepId && keepId === partnerOf(lid) ? keepId : lid;
            wChildren.push({kind: "stick", stick: {id: mergedId}});
        }
    }

    // transpose in place when the parent is exactly [L, S, R]; otherwise wrap the
    // [L, S, R] run in a new block of the opposite orientation, leaving siblings.
    if(P.children.length === 3 && sIdx === 1) {
        P.kind = colKind;
        P.children = wChildren;
    } else {
        const wrapper: MdFrameBlock = {
            id: nextId(spec, "block"),
            kind: colKind,
            size: new FrmVar(nextId(spec, "block-size"), wrapExtent),
            children: wChildren,
        };
        P.children.splice(sIdx - 1, 3, {kind: "block", block: wrapper});
    }
    return true;
}

// a direct child stick of `block` with this id (the capping sticks live as direct
// children of the neighbour cell).
function directStick(block: MdFrameBlock, id: string): boolean {
    return block.children.some(c => c.kind === "stick" && c.stick.id === id);
}

// split a block's children into segments at the sticks whose ids are in `ids`
// (in document order). Returns segments.length === splitSticks.length + 1.
function splitAtSticks(block: MdFrameBlock, ids: Set<string>): {segments: MdFrameBlockChild[][]; splitSticks: MdFrameStick[]} {
    const segments: MdFrameBlockChild[][] = [];
    const splitSticks: MdFrameStick[] = [];
    let cur: MdFrameBlockChild[] = [];

    for(const c of block.children) {
        if(c.kind === "stick" && ids.has(c.stick.id)) {
            segments.push(cur);
            cur = [];
            splitSticks.push(c.stick);
        } else {
            cur.push(c);
        }
    }
    segments.push(cur);
    return {segments, splitSticks};
}

// ---- distribute (equalize flow sizes) ---------------------------------------
// Distribute makes a run of sibling cells share the parent's flow equally,
// preserving their combined extent (so the parent and any unselected siblings
// don't shift). The flow size lives on each cell's FrmVar — item.size for a
// lite/panel, block.size for a nested block — and userSet pins it so the solver
// honours it (mirrors makeMullionResize, which reallocates between two neighbours).

// a cell whose flow size can be distributed: a lite/panel item, or a nested block.
// A door (immovable) and a stick (a separator, not a cell) are not resizable.
function isResizableCell(child: MdFrameBlockChild | undefined): boolean {
    if(!child) return false;
    if(child.kind === "item") return child.item.kind !== "doors";
    return child.kind === "block";
}

// the flow FrmVar for a resizable cell, materialized if absent. Only called right
// before a userSet (so a 0 seed is immediately overwritten). Null for a non-cell.
function cellFlowVar(spec: MdFrameSpec, child: MdFrameBlockChild): FrmVar | null {
    if(child.kind === "item") {
        if(child.item.kind === "doors") return null;
        if(!child.item.size) child.item.size = new FrmVar(nextId(spec, "dist-size"), 0);
        return child.item.size;
    }
    if(child.kind === "block") return child.block.size;
    return null;
}

function cellId(child: MdFrameBlockChild): string | null {
    if(child.kind === "item") return child.item.id;
    if(child.kind === "block") return child.block.id;
    return null;
}

// set every cell in `run` to the average of their current resolved sizes on the
// parent-flow axis, preserving their total. Returns true when a change was made.
function distributeRun(spec: MdFrameSpec, run: MdFrameBlockChild[], axis: "width" | "height"): boolean {
    if(run.length < 2) return false;

    const sizes = run.map(c => spec.sizeInfo.get(cellId(c) ?? "")?.[axis] ?? 0);
    const total = sizes.reduce((a, b) => a + b, 0);
    if(total <= 0) return false;

    const avg = total / run.length;
    let changed = false;
    for(const c of run) {
        const v = cellFlowVar(spec, c);
        if(v) { v.userSet(avg, {debug: "distribute"}); changed = true; }
    }
    return changed;
}

// equalize the selected sibling cells. Selected cells are grouped by their parent
// block (a row equalizes widths, a column heights); within each parent only
// *contiguous* runs of selected cells are equalized, so an unselected cell between
// two selected ones splits them into separate runs and stays put.
export function distributeCells(spec: MdFrameSpec, ids: string[]): boolean {
    const idSet = new Set(ids);

    // the distinct parent blocks that hold a selected cell.
    const parents = new Set<MdFrameBlock>();
    for(const id of ids) {
        const path = findItemPath(spec.frameBlocks, id);
        if(path) parents.add(path[path.length - 1].block);
    }

    let changed = false;
    for(const parent of parents) {
        const axis = parent.kind === "row" ? "width" : "height";
        let run: MdFrameBlockChild[] = [];
        const flush = () => { if(distributeRun(spec, run, axis)) changed = true; run = []; };

        for(const child of parent.children) {
            if(!isCell(child)) continue;
            const id = cellId(child);
            if(id && idSet.has(id) && isResizableCell(child)) run.push(child);
            else flush();
        }
        flush();
    }
    return changed;
}

// equalize the cells bounded by the selected sticks. Selected sticks are grouped by
// parent block; a run of adjacent selected sticks (consecutive separators) bounds
// the cells immediately around it, which are then equalized. e.g. selecting all
// three mullions dividing a column of four cells equalizes those four cells.
export function distributeSticks(spec: MdFrameSpec, ids: string[]): boolean {
    // stick child-indices per parent block.
    const groups = new Map<MdFrameBlock, number[]>();
    for(const id of ids) {
        const path = findStickPath(spec.frameBlocks, id);
        if(!path) continue;
        const arr = groups.get(path.block) ?? [];
        arr.push(path.index);
        groups.set(path.block, arr);
    }

    let changed = false;
    for(const [parent, stickIdx] of groups) {
        const axis = parent.kind === "row" ? "width" : "height";
        stickIdx.sort((a, b) => a - b);

        // group adjacent sticks: two are adjacent when a single cell sits between
        // them (child indices differ by 2). Each run bounds the cells from the one
        // before its first stick through the one after its last.
        let run: number[] = [];
        const flush = () => {
            if(run.length > 0) {
                const cells = boundedCells(parent, run[0], run[run.length - 1]);
                if(cells && distributeRun(spec, cells, axis)) changed = true;
            }
            run = [];
        };
        for(const idx of stickIdx) {
            if(run.length === 0 || idx === run[run.length - 1] + 2) run.push(idx);
            else { flush(); run = [idx]; }
        }
        flush();
    }
    return changed;
}

// the cells around a contiguous stick run (first..last child-indices): the cell
// left of the first stick through the cell right of the last, stepping by 2 over
// the alternating cell/stick children. Null if any is a non-cell (e.g. a door).
function boundedCells(parent: MdFrameBlock, first: number, last: number): MdFrameBlockChild[] | null {
    const cells: MdFrameBlockChild[] = [];
    for(let i = first - 1; i <= last + 1; i += 2) {
        const c = parent.children[i];
        if(!c || !isResizableCell(c)) return null;
        cells.push(c);
    }
    return cells;
}

// ---- block-size edits --------------------------------------------------------
// When a block's extent on an axis changes (the overall frame dimension, a dragged
// outline or mullion, a block dimension edit), the change has to land on the cells
// inside it. Unpinned cells share it in proportion to their sizes, so they all scale
// at one rate and mullions that lined up before stay lined up. Left to the solver,
// its linear objective would drop the whole change on one of them. When every
// resizable cell along the axis is pinned there's nowhere for the change to go and
// the cells go stale — so push the delta into the pinned cells explicitly, in
// priority order: the right-most first for a width edit, the top-most first for a
// height edit. A grow lands entirely on the first cell; a shrink drains each cell
// down to the solver minimum before moving to the next.

// smallest a cell may be shrunk to — matches the solver's required minimum.
const MIN_CELL = 3 * inches;

// whether a cell can take a size change on `axis`. A door's dimensions are
// dictated. A block passes a change along its own flow into any one absorbing
// cell; across its flow every child is forced to follow (equality), so all of
// them must be able to absorb.
function cellCanAbsorb(child: MdFrameBlockChild, axis: "width" | "height"): boolean {
    if(child.kind === "item") return child.item.kind !== "doors";
    if(child.kind !== "block") return false;

    const flowMatches = (child.block.kind === "row") === (axis === "width");
    const cells = child.block.children.filter(isCell);
    if(cells.length === 0) return false;
    return flowMatches
        ? cells.some(c => cellCanAbsorb(c, axis))
        : cells.every(c => cellCanAbsorb(c, axis));
}

// whether the solver can reflow this cell on `axis` by itself, with no pin in the
// way. `flowAxis` says whether `axis` is governed by the cell's own size var (its
// parent's flow); across it the cell follows its parent by equality and the
// question moves inward — a wrapper block with an unpinned size but a fully
// pinned interior is NOT free.
function cellSolverFree(child: MdFrameBlockChild, axis: "width" | "height", flowAxis: boolean): boolean {
    if(child.kind === "item") {
        if(child.item.kind === "doors") return false;
        return !flowAxis || !child.item.size || !child.item.size.isModified();
    }
    if(child.kind !== "block") return false;

    if(flowAxis && child.block.size.isModified()) return false;
    const inner = (child.block.kind === "row") === (axis === "width");
    const cells = child.block.children.filter(isCell);
    if(cells.length === 0) return false;
    return inner
        ? cells.some(c => cellSolverFree(c, axis, true))
        : cells.every(c => cellSolverFree(c, axis, false));
}

export function absorbOuterSizeDelta(spec: MdFrameSpec, axis: "width" | "height", delta: number) {
    absorbSizeDelta(spec, spec.frameBlocks, axis, delta);
}

// `block`'s extent on `axis` is changing by `delta`: land the change on its cells.
export function absorbSizeDelta(spec: MdFrameSpec, block: MdFrameBlock, axis: "width" | "height", delta: number) {
    if(delta === 0) return;
    absorbInto(spec, block, axis, delta);
}

function absorbInto(spec: MdFrameSpec, block: MdFrameBlock, axis: "width" | "height", delta: number) {
    const flowMatches = (block.kind === "row") === (axis === "width");

    if(!flowMatches) {
        // cross axis: every cell spans the block's full extent, so each nested
        // block absorbs the delta itself; leaf items follow the solver's
        // cross-axis equality.
        for(const c of block.children) {
            if(c.kind === "block") absorbInto(spec, c.block, axis, delta);
        }
        return;
    }

    // flow axis: only cells that can actually take the change on this axis (a
    // door-dictated extent can't).
    const candidates = block.children.filter(c => isCell(c) && cellCanAbsorb(c, axis));
    if(candidates.length === 0) return;

    // solver-free cells take the change, shared in proportion to their sizes.
    const free = candidates.filter(c => cellSolverFree(c, axis, true));
    if(free.length > 0) {
        scaleCells(spec, free, axis, delta);
        return;
    }

    const ordered = axis === "width" ? [...candidates].reverse() : candidates;

    let remaining = delta;
    for(const target of ordered) {
        if(remaining === 0) break;

        const v = cellFlowVar(spec, target);
        if(!v) continue;

        const cur = v.get();
        const next = Math.max(MIN_CELL, cur + remaining);
        if(next !== cur) {
            v.userSet(next, {debug: "outer-size-edit"});
            // a nested-block target only resizes as a unit here; the blocks inside
            // it (e.g. the rows of a cross-flow column) must absorb its share too.
            if(target.kind === "block") absorbInto(spec, target.block, axis, next - cur);
        }
        remaining -= next - cur;
    }
}

// grow or shrink `cells` by `delta` in total, each in proportion to its size. A cell
// that would go under MIN_CELL stops there and the others take its remainder. Sizes
// are written soft (set), so the cells stay solver-owned; a nested block passes its
// share inward.
function scaleCells(spec: MdFrameSpec, cells: MdFrameBlockChild[], axis: "width" | "height", delta: number) {
    let open = cells.map(c => ({c, v: cellFlowVar(spec, c)})).filter(x => !!x.v) as {c: MdFrameBlockChild; v: FrmVar}[];
    let remaining = delta;

    while(open.length > 0 && remaining !== 0) {
        const total = open.reduce((sum, x) => sum + x.v.get(), 0);
        if(total <= 0) return;

        const share = remaining;
        const next: typeof open = [];
        remaining = 0;

        for(const x of open) {
            const cur = x.v.get();
            const want = cur + share * cur / total;
            const to = Math.max(MIN_CELL, want);
            remaining += want - to;

            if(to !== cur) {
                x.v.set(to, {debug: "scale-cells"});
                if(x.c.kind === "block") absorbInto(spec, x.c.block, axis, to - cur);
            }
            if(to > MIN_CELL) next.push(x);
        }
        open = next;
    }
}

// a segment becomes a single cell directly when it holds exactly one cell, or a
// wrapper block (preserving its internal splits) when it holds more. `seed` is the
// wrapper's footprint along the parent flow (its cross-axis extent before the flip).
function unwrapSegment(spec: MdFrameSpec, seg: MdFrameBlockChild[], kind: "row" | "column", seed: number): MdFrameBlockChild {
    if(seg.length === 1 && (seg[0].kind === "item" || seg[0].kind === "block")) return seg[0];
    return {
        kind: "block",
        block: {id: nextId(spec, "block"), kind, size: new FrmVar(nextId(spec, "block-size"), seed), children: seg},
    };
}

// --- door slots (multi-opening screens) ---

// the width a new door slot cell starts at when the cell's laid-out size isn't known.
const NEW_DOOR_SLOT_WIDTH = 36 * inches;

// turn a lite / fixed panel into a door cell (doors over threshold) in the same place. The doors
// item keeps the cell's laid-out width as its design width; wireDoorsItem gives it the primary's
// door height. Returns the new doors item id, or null when the id isn't a lite or panel.
function makeDoorCell(spec: MdFrameSpec, itemId: string): string | null {
    const path = findItemPath(spec.frameBlocks, itemId);
    if(!path) return null;

    const step = path[path.length - 1];
    const ref = step.block.children[step.index];
    if(ref.kind !== "item" || (ref.item.kind !== "lite" && ref.item.kind !== "fixedPanel")) return null;

    // a door cell forces nothing, so withdraw whatever the outgoing fill had stamped.
    applyForcedProfile(spec, itemId, undefined, forcedProfile(ref.item));

    const width = spec.sizeInfo.get(itemId)?.width || NEW_DOOR_SLOT_WIDTH;
    const doorsId = nextId(spec, "doors");
    const cell: MdFrameBlock = {
        id: nextId(spec, "door"),
        kind: "column",
        size: new FrmVar(nextId(spec, "door-size"), 0),
        children: [
            {kind: "item", item: {id: doorsId, kind: "doors", width: new FrmVar(doorsId + ":w", width), height: new FrmVar(doorsId + ":h", 0)}},
            {kind: "item", item: {id: nextId(spec, "threshold"), kind: "threshold", size: spec.thresholdHeight}},
        ],
    };

    step.block.children[step.index] = {kind: "block", block: cell};
    return doorsId;
}

// turn the door cell holding this doors item back into a lite in the same place. Returns false
// when the doors item isn't in a door cell.
function removeDoorCell(spec: MdFrameSpec, doorsId: string): boolean {
    const path = findItemPath(spec.frameBlocks, doorsId);
    if(!path || path.length < 2) return false;

    const cellStep = path[path.length - 1];
    if(!isDoorCell(cellStep.block)) return false;

    const parentStep = path[path.length - 2];
    const width = spec.sizeInfo.get(doorsId)?.width;
    const lite = newLiteCell(spec, width || NEW_LITE_SIZE, false);
    parentStep.block.children[parentStep.index] = lite;
    return true;
}

// the next free slot name: A, B, C, … (case-insensitive, like the server's uniqueness rule).
function nextSlotName(spec: MdFrameSpec): string {
    const taken = new Set(spec.slots.map(s => s.name.toLowerCase()));
    for(let i = 0; ; i++) {
        let name = "";
        let n = i;
        do {
            name = String.fromCharCode(65 + (n % 26)) + name;
            n = Math.floor(n / 26) - 1;
        } while(n >= 0);

        if(!taken.has(name.toLowerCase())) return name;
    }
}

// make the lite / fixed panel a new door slot. The first slot also gives the primary's door
// cell slot "A". Returns the new slot's name, or null when the cell can't hold a door slot.
export function makeDoorSlot(spec: MdFrameSpec, itemId: string): string | null {
    if(spec.slots.length === 0) {
        const primary = primaryDoorsItem(spec);
        if(!primary) return null;

        spec.slots.push({name: "A", cell: primary.id});
        spec.primarySlot = "A";
    }

    const doorsId = makeDoorCell(spec, itemId);
    if(!doorsId) return null;

    const name = nextSlotName(spec);
    spec.slots.push({name, cell: doorsId});

    wireDoorsItem(spec);
    normalizeEdgeSticks(spec);
    return name;
}

// delete a secondary door slot; its door cell becomes a lite again. Deleting the last secondary
// slot makes the screen a single-door screen again. The primary slot can't be deleted.
export function removeDoorSlot(spec: MdFrameSpec, name: string): boolean {
    if(name === spec.primarySlot) return false;

    const slot = spec.slots.find(s => s.name === name);
    if(!slot) return false;

    if(slot.cell) removeDoorCell(spec, slot.cell);
    spec.slots = spec.slots.filter(s => s !== slot);

    if(spec.slots.length === 1) {
        spec.slots = [];
        spec.primarySlot = undefined;
    }

    wireDoorsItem(spec);
    normalizeEdgeSticks(spec);
    spec.assignLiteMarks();
    return true;
}

// move a door slot to the lite / fixed panel: the slot's old door cell becomes a lite, and the
// target becomes its door cell.
export function moveDoorSlot(spec: MdFrameSpec, name: string, itemId: string): boolean {
    const slot = spec.slots.find(s => s.name === name);
    if(!slot) return false;

    const doorsId = makeDoorCell(spec, itemId);
    if(!doorsId) return false;

    if(slot.cell) removeDoorCell(spec, slot.cell);
    slot.cell = doorsId;

    wireDoorsItem(spec);
    normalizeEdgeSticks(spec);
    spec.assignLiteMarks();
    return true;
}

// rename a door slot. Throws when the name is empty or another slot has it.
export function renameDoorSlot(spec: MdFrameSpec, from: string, to: string) {
    const name = to.trim();
    if(!name) throw new Error("A door slot needs a name.");
    if(spec.slots.some(s => s.name !== from && s.name.toLowerCase() === name.toLowerCase())) {
        throw new Error(`Door slot "${name}" already exists.`);
    }

    const slot = spec.slots.find(s => s.name === from);
    if(!slot) return;

    slot.name = name;
    if(spec.primarySlot === from) spec.primarySlot = name;
}
